export type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[]

export type Database = {
  // Allows to automatically instantiate createClient with right options
  // instead of createClient<Database, { PostgrestVersion: 'XX' }>(URL, KEY)
  __InternalSupabase: {
    PostgrestVersion: '14.5'
  }
  public: {
    Tables: {
      account_email_verifications: {
        Row: {
          attempts: number
          consumed_at: string | null
          created_at: string
          email: string
          expires_at: string
          id: string
          sent_count: number
          token_hash: string
          updated_at: string
          user_id: string
        }
        Insert: {
          attempts?: number
          consumed_at?: string | null
          created_at?: string
          email: string
          expires_at: string
          id?: string
          sent_count?: number
          token_hash: string
          updated_at?: string
          user_id: string
        }
        Update: {
          attempts?: number
          consumed_at?: string | null
          created_at?: string
          email?: string
          expires_at?: string
          id?: string
          sent_count?: number
          token_hash?: string
          updated_at?: string
          user_id?: string
        }
        Relationships: []
      }
      addresses: {
        Row: {
          city: string
          country_code: string
          created_at: string
          id: string
          is_default: boolean
          label: string
          location: unknown
          postal_code: string | null
          profile_id: string
          region: string | null
          street_line1: string
          street_line2: string | null
          updated_at: string
        }
        Insert: {
          city: string
          country_code: string
          created_at?: string
          id?: string
          is_default?: boolean
          label: string
          location?: unknown
          postal_code?: string | null
          profile_id: string
          region?: string | null
          street_line1: string
          street_line2?: string | null
          updated_at?: string
        }
        Update: {
          city?: string
          country_code?: string
          created_at?: string
          id?: string
          is_default?: boolean
          label?: string
          location?: unknown
          postal_code?: string | null
          profile_id?: string
          region?: string | null
          street_line1?: string
          street_line2?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'addresses_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      aml_declarations: {
        Row: {
          created_at: string
          date_of_birth: string
          disclaimer_accepted_at: string | null
          disclaimer_scroll_completed: boolean
          disclaimer_version: string
          economic_activity: string
          exported_to_partner_at: string | null
          full_name: string
          fund_origin: Database['public']['Enums']['aml_fund_origin']
          gender: Database['public']['Enums']['aml_gender']
          has_illicit_or_judicial_history: boolean
          has_pep_associate: boolean
          has_pep_family: boolean
          has_tax_haven_relations: boolean
          id: string
          income_range: Database['public']['Enums']['aml_income_range']
          is_pep: boolean
          is_political_party_member: boolean
          nationality_country_id: string
          origin_country_id: string | null
          partner_reference: string | null
          phone: string
          profile_id: string
          rejection_reason: string | null
          residence_country_id: string
          review_notes: string | null
          reviewed_at: string | null
          reviewed_by: string | null
          status: Database['public']['Enums']['aml_declaration_status']
          submitted_at: string | null
          updated_at: string
        }
        Insert: {
          created_at?: string
          date_of_birth: string
          disclaimer_accepted_at?: string | null
          disclaimer_scroll_completed?: boolean
          disclaimer_version: string
          economic_activity: string
          exported_to_partner_at?: string | null
          full_name: string
          fund_origin: Database['public']['Enums']['aml_fund_origin']
          gender: Database['public']['Enums']['aml_gender']
          has_illicit_or_judicial_history: boolean
          has_pep_associate: boolean
          has_pep_family: boolean
          has_tax_haven_relations: boolean
          id?: string
          income_range: Database['public']['Enums']['aml_income_range']
          is_pep: boolean
          is_political_party_member: boolean
          nationality_country_id: string
          origin_country_id?: string | null
          partner_reference?: string | null
          phone: string
          profile_id: string
          rejection_reason?: string | null
          residence_country_id: string
          review_notes?: string | null
          reviewed_at?: string | null
          reviewed_by?: string | null
          status?: Database['public']['Enums']['aml_declaration_status']
          submitted_at?: string | null
          updated_at?: string
        }
        Update: {
          created_at?: string
          date_of_birth?: string
          disclaimer_accepted_at?: string | null
          disclaimer_scroll_completed?: boolean
          disclaimer_version?: string
          economic_activity?: string
          exported_to_partner_at?: string | null
          full_name?: string
          fund_origin?: Database['public']['Enums']['aml_fund_origin']
          gender?: Database['public']['Enums']['aml_gender']
          has_illicit_or_judicial_history?: boolean
          has_pep_associate?: boolean
          has_pep_family?: boolean
          has_tax_haven_relations?: boolean
          id?: string
          income_range?: Database['public']['Enums']['aml_income_range']
          is_pep?: boolean
          is_political_party_member?: boolean
          nationality_country_id?: string
          origin_country_id?: string | null
          partner_reference?: string | null
          phone?: string
          profile_id?: string
          rejection_reason?: string | null
          residence_country_id?: string
          review_notes?: string | null
          reviewed_at?: string | null
          reviewed_by?: string | null
          status?: Database['public']['Enums']['aml_declaration_status']
          submitted_at?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'aml_declarations_nationality_country_id_fkey'
            columns: ['nationality_country_id']
            isOneToOne: false
            referencedRelation: 'countries'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'aml_declarations_origin_country_id_fkey'
            columns: ['origin_country_id']
            isOneToOne: false
            referencedRelation: 'countries'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'aml_declarations_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'aml_declarations_residence_country_id_fkey'
            columns: ['residence_country_id']
            isOneToOne: false
            referencedRelation: 'countries'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'aml_declarations_reviewed_by_fkey'
            columns: ['reviewed_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      aml_screenings: {
        Row: {
          answered_at: string | null
          created_at: string
          declaration_id: string | null
          id: string
          identity_document_id: string | null
          outcome: string
          profile_id: string
          provider: string
          provider_reference: string | null
          reason: string | null
          requested_at: string
        }
        Insert: {
          answered_at?: string | null
          created_at?: string
          declaration_id?: string | null
          id?: string
          identity_document_id?: string | null
          outcome?: string
          profile_id: string
          provider?: string
          provider_reference?: string | null
          reason?: string | null
          requested_at?: string
        }
        Update: {
          answered_at?: string | null
          created_at?: string
          declaration_id?: string | null
          id?: string
          identity_document_id?: string | null
          outcome?: string
          profile_id?: string
          provider?: string
          provider_reference?: string | null
          reason?: string | null
          requested_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'aml_screenings_declaration_id_fkey'
            columns: ['declaration_id']
            isOneToOne: false
            referencedRelation: 'aml_declarations'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'aml_screenings_identity_document_id_fkey'
            columns: ['identity_document_id']
            isOneToOne: false
            referencedRelation: 'identity_documents'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'aml_screenings_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      analytics_excluded_profiles: {
        Row: {
          created_at: string
          excluded_at: string
          profile_id: string
          reason: string
          source: string
        }
        Insert: {
          created_at?: string
          excluded_at?: string
          profile_id: string
          reason: string
          source?: string
        }
        Update: {
          created_at?: string
          excluded_at?: string
          profile_id?: string
          reason?: string
          source?: string
        }
        Relationships: [
          {
            foreignKeyName: 'analytics_excluded_profiles_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: true
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      arco_requests: {
        Row: {
          actions_taken: Json
          assigned_to: string | null
          completed_at: string | null
          created_at: string
          denial_reason: string | null
          description: string
          due_at: string
          extended_until: string | null
          id: string
          identity_verified: boolean
          profile_id: string | null
          received_at: string
          request_type: Database['public']['Enums']['arco_request_type']
          requester_email: string
          requester_name: string
          response: string | null
          status: Database['public']['Enums']['arco_status']
          updated_at: string
          verified_at: string | null
        }
        Insert: {
          actions_taken?: Json
          assigned_to?: string | null
          completed_at?: string | null
          created_at?: string
          denial_reason?: string | null
          description: string
          due_at?: string
          extended_until?: string | null
          id?: string
          identity_verified?: boolean
          profile_id?: string | null
          received_at?: string
          request_type: Database['public']['Enums']['arco_request_type']
          requester_email: string
          requester_name: string
          response?: string | null
          status?: Database['public']['Enums']['arco_status']
          updated_at?: string
          verified_at?: string | null
        }
        Update: {
          actions_taken?: Json
          assigned_to?: string | null
          completed_at?: string | null
          created_at?: string
          denial_reason?: string | null
          description?: string
          due_at?: string
          extended_until?: string | null
          id?: string
          identity_verified?: boolean
          profile_id?: string | null
          received_at?: string
          request_type?: Database['public']['Enums']['arco_request_type']
          requester_email?: string
          requester_name?: string
          response?: string | null
          status?: Database['public']['Enums']['arco_status']
          updated_at?: string
          verified_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'arco_requests_assigned_to_fkey'
            columns: ['assigned_to']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'arco_requests_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      audit_logs: {
        Row: {
          action: string
          actor_id: string | null
          actor_type: string
          created_at: string
          id: string
          ip_address: unknown
          new_values: Json | null
          old_values: Json | null
          resource_id: string | null
          resource_type: string
          user_agent: string | null
        }
        Insert: {
          action: string
          actor_id?: string | null
          actor_type?: string
          created_at?: string
          id?: string
          ip_address?: unknown
          new_values?: Json | null
          old_values?: Json | null
          resource_id?: string | null
          resource_type: string
          user_agent?: string | null
        }
        Update: {
          action?: string
          actor_id?: string | null
          actor_type?: string
          created_at?: string
          id?: string
          ip_address?: unknown
          new_values?: Json | null
          old_values?: Json | null
          resource_id?: string | null
          resource_type?: string
          user_agent?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'audit_logs_actor_id_fkey'
            columns: ['actor_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      background_jobs: {
        Row: {
          attempts: number
          completed_at: string | null
          created_at: string
          error: string | null
          failed_at: string | null
          id: string
          job_type: string
          max_attempts: number
          payload: Json
          priority: number
          result: Json | null
          scheduled_for: string
          started_at: string | null
          status: string
          updated_at: string
        }
        Insert: {
          attempts?: number
          completed_at?: string | null
          created_at?: string
          error?: string | null
          failed_at?: string | null
          id?: string
          job_type: string
          max_attempts?: number
          payload?: Json
          priority?: number
          result?: Json | null
          scheduled_for?: string
          started_at?: string | null
          status?: string
          updated_at?: string
        }
        Update: {
          attempts?: number
          completed_at?: string | null
          created_at?: string
          error?: string | null
          failed_at?: string | null
          id?: string
          job_type?: string
          max_attempts?: number
          payload?: Json
          priority?: number
          result?: Json | null
          scheduled_for?: string
          started_at?: string | null
          status?: string
          updated_at?: string
        }
        Relationships: []
      }
      cities: {
        Row: {
          created_at: string
          id: string
          is_active: boolean
          location: unknown
          municipality_id: string | null
          name: string
          region_id: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          id?: string
          is_active?: boolean
          location?: unknown
          municipality_id?: string | null
          name: string
          region_id: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          id?: string
          is_active?: boolean
          location?: unknown
          municipality_id?: string | null
          name?: string
          region_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'cities_municipality_id_fkey'
            columns: ['municipality_id']
            isOneToOne: false
            referencedRelation: 'municipalities'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'cities_region_id_fkey'
            columns: ['region_id']
            isOneToOne: false
            referencedRelation: 'regions'
            referencedColumns: ['id']
          },
        ]
      }
      consent_records: {
        Row: {
          consent_form_url: string | null
          consent_form_version: string
          consent_type: string
          created_at: string
          expires_at: string | null
          granted: boolean
          granted_at: string
          id: string
          ip_address: unknown
          legal_basis: string
          profile_id: string
          purpose: string
          revoked_at: string | null
          updated_at: string
          user_agent: string | null
        }
        Insert: {
          consent_form_url?: string | null
          consent_form_version: string
          consent_type: string
          created_at?: string
          expires_at?: string | null
          granted: boolean
          granted_at?: string
          id?: string
          ip_address?: unknown
          legal_basis: string
          profile_id: string
          purpose: string
          revoked_at?: string | null
          updated_at?: string
          user_agent?: string | null
        }
        Update: {
          consent_form_url?: string | null
          consent_form_version?: string
          consent_type?: string
          created_at?: string
          expires_at?: string | null
          granted?: boolean
          granted_at?: string
          id?: string
          ip_address?: unknown
          legal_basis?: string
          profile_id?: string
          purpose?: string
          revoked_at?: string | null
          updated_at?: string
          user_agent?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'consent_records_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      conversations: {
        Row: {
          created_at: string
          id: string
          is_archived: boolean
          last_message_at: string | null
          last_message_preview: string | null
          participant_ids: string[]
          rental_id: string | null
          updated_at: string
        }
        Insert: {
          created_at?: string
          id?: string
          is_archived?: boolean
          last_message_at?: string | null
          last_message_preview?: string | null
          participant_ids: string[]
          rental_id?: string | null
          updated_at?: string
        }
        Update: {
          created_at?: string
          id?: string
          is_archived?: boolean
          last_message_at?: string | null
          last_message_preview?: string | null
          participant_ids?: string[]
          rental_id?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'conversations_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      coverage_eligibility_rules: {
        Row: {
          created_at: string
          eligible_providers: string[]
          id: string
          is_active: boolean
          own_policy_allowed: boolean
          platform_exemption_allowed: boolean
          rationale: string | null
          updated_at: string
          vehicle_category: string
        }
        Insert: {
          created_at?: string
          eligible_providers?: string[]
          id?: string
          is_active?: boolean
          own_policy_allowed?: boolean
          platform_exemption_allowed?: boolean
          rationale?: string | null
          updated_at?: string
          vehicle_category: string
        }
        Update: {
          created_at?: string
          eligible_providers?: string[]
          id?: string
          is_active?: boolean
          own_policy_allowed?: boolean
          platform_exemption_allowed?: boolean
          rationale?: string | null
          updated_at?: string
          vehicle_category?: string
        }
        Relationships: []
      }
      countries: {
        Row: {
          code: string
          consumer_protection_law: string | null
          created_at: string
          currency_code: string
          data_protection_law: string | null
          default_insurance_model: Database['public']['Enums']['insurance_model']
          dte_required: boolean
          id: string
          is_active: boolean
          locale: string
          min_platform_fee_cents: number
          multa_liability_default: Database['public']['Enums']['multa_liability']
          name: string
          phone_prefix: string
          platform_fee_bps: number
          requires_insurance: boolean
          timezone: string
          updated_at: string
        }
        Insert: {
          code: string
          consumer_protection_law?: string | null
          created_at?: string
          currency_code?: string
          data_protection_law?: string | null
          default_insurance_model?: Database['public']['Enums']['insurance_model']
          dte_required?: boolean
          id?: string
          is_active?: boolean
          locale?: string
          min_platform_fee_cents?: number
          multa_liability_default?: Database['public']['Enums']['multa_liability']
          name: string
          phone_prefix: string
          platform_fee_bps?: number
          requires_insurance?: boolean
          timezone?: string
          updated_at?: string
        }
        Update: {
          code?: string
          consumer_protection_law?: string | null
          created_at?: string
          currency_code?: string
          data_protection_law?: string | null
          default_insurance_model?: Database['public']['Enums']['insurance_model']
          dte_required?: boolean
          id?: string
          is_active?: boolean
          locale?: string
          min_platform_fee_cents?: number
          multa_liability_default?: Database['public']['Enums']['multa_liability']
          name?: string
          phone_prefix?: string
          platform_fee_bps?: number
          requires_insurance?: boolean
          timezone?: string
          updated_at?: string
        }
        Relationships: []
      }
      damage_report_images: {
        Row: {
          caption: string | null
          created_at: string
          damage_report_id: string
          id: string
          storage_path: string
          url: string
        }
        Insert: {
          caption?: string | null
          created_at?: string
          damage_report_id: string
          id?: string
          storage_path: string
          url: string
        }
        Update: {
          caption?: string | null
          created_at?: string
          damage_report_id?: string
          id?: string
          storage_path?: string
          url?: string
        }
        Relationships: [
          {
            foreignKeyName: 'damage_report_images_damage_report_id_fkey'
            columns: ['damage_report_id']
            isOneToOne: false
            referencedRelation: 'damage_reports'
            referencedColumns: ['id']
          },
        ]
      }
      damage_reports: {
        Row: {
          actual_repair_cents: number | null
          assistance_case_number: string | null
          assistance_phone: string | null
          checkin_inspection_id: string | null
          checkout_inspection_id: string | null
          created_at: string
          damage_areas: string[]
          deductible_cents: number | null
          description: string
          estimated_repair_cents: number | null
          guest_liability_cents: number | null
          id: string
          insurance_claim_cents: number | null
          rental_id: string
          reported_at: string
          reported_by: string
          resolved_at: string | null
          severity: Database['public']['Enums']['damage_severity']
          status: string
          updated_at: string
          vehicle_id: string
        }
        Insert: {
          actual_repair_cents?: number | null
          assistance_case_number?: string | null
          assistance_phone?: string | null
          checkin_inspection_id?: string | null
          checkout_inspection_id?: string | null
          created_at?: string
          damage_areas?: string[]
          deductible_cents?: number | null
          description: string
          estimated_repair_cents?: number | null
          guest_liability_cents?: number | null
          id?: string
          insurance_claim_cents?: number | null
          rental_id: string
          reported_at?: string
          reported_by: string
          resolved_at?: string | null
          severity: Database['public']['Enums']['damage_severity']
          status?: string
          updated_at?: string
          vehicle_id: string
        }
        Update: {
          actual_repair_cents?: number | null
          assistance_case_number?: string | null
          assistance_phone?: string | null
          checkin_inspection_id?: string | null
          checkout_inspection_id?: string | null
          created_at?: string
          damage_areas?: string[]
          deductible_cents?: number | null
          description?: string
          estimated_repair_cents?: number | null
          guest_liability_cents?: number | null
          id?: string
          insurance_claim_cents?: number | null
          rental_id?: string
          reported_at?: string
          reported_by?: string
          resolved_at?: string | null
          severity?: Database['public']['Enums']['damage_severity']
          status?: string
          updated_at?: string
          vehicle_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'damage_reports_checkin_inspection_id_fkey'
            columns: ['checkin_inspection_id']
            isOneToOne: false
            referencedRelation: 'rental_inspections'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'damage_reports_checkout_inspection_id_fkey'
            columns: ['checkout_inspection_id']
            isOneToOne: false
            referencedRelation: 'rental_inspections'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'damage_reports_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'damage_reports_reported_by_fkey'
            columns: ['reported_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'damage_reports_vehicle_id_fkey'
            columns: ['vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
        ]
      }
      deposit_holds: {
        Row: {
          amount_cents: number
          claim_amount_cents: number | null
          claim_reason: string | null
          claimed_at: string | null
          created_at: string
          currency: string
          held_at: string
          id: string
          payment_method_id: string
          provider: string
          provider_hold_id: string | null
          release_amount_cents: number | null
          released_at: string | null
          rental_id: string
          status: string
          updated_at: string
        }
        Insert: {
          amount_cents: number
          claim_amount_cents?: number | null
          claim_reason?: string | null
          claimed_at?: string | null
          created_at?: string
          currency?: string
          held_at?: string
          id?: string
          payment_method_id: string
          provider?: string
          provider_hold_id?: string | null
          release_amount_cents?: number | null
          released_at?: string | null
          rental_id: string
          status?: string
          updated_at?: string
        }
        Update: {
          amount_cents?: number
          claim_amount_cents?: number | null
          claim_reason?: string | null
          claimed_at?: string | null
          created_at?: string
          currency?: string
          held_at?: string
          id?: string
          payment_method_id?: string
          provider?: string
          provider_hold_id?: string | null
          release_amount_cents?: number | null
          released_at?: string | null
          rental_id?: string
          status?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'deposit_holds_payment_method_id_fkey'
            columns: ['payment_method_id']
            isOneToOne: false
            referencedRelation: 'payment_methods'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'deposit_holds_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      dispute_settlements: {
        Row: {
          action: Database['public']['Enums']['dispute_settlement_action']
          amount_cents: number
          attempt_count: number
          claimed_at: string | null
          completed_at: string | null
          created_at: string
          dispute_id: string
          id: string
          last_error: string | null
          provider_transaction_id: string | null
          rental_id: string
          status: Database['public']['Enums']['dispute_settlement_status']
          updated_at: string
        }
        Insert: {
          action: Database['public']['Enums']['dispute_settlement_action']
          amount_cents?: number
          attempt_count?: number
          claimed_at?: string | null
          completed_at?: string | null
          created_at?: string
          dispute_id: string
          id?: string
          last_error?: string | null
          provider_transaction_id?: string | null
          rental_id: string
          status?: Database['public']['Enums']['dispute_settlement_status']
          updated_at?: string
        }
        Update: {
          action?: Database['public']['Enums']['dispute_settlement_action']
          amount_cents?: number
          attempt_count?: number
          claimed_at?: string | null
          completed_at?: string | null
          created_at?: string
          dispute_id?: string
          id?: string
          last_error?: string | null
          provider_transaction_id?: string | null
          rental_id?: string
          status?: Database['public']['Enums']['dispute_settlement_status']
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'dispute_settlements_dispute_id_fkey'
            columns: ['dispute_id']
            isOneToOne: false
            referencedRelation: 'rental_disputes'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'dispute_settlements_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      driver_licenses: {
        Row: {
          back_image_path: string | null
          canonical_status: string | null
          confidence: number | null
          created_at: string
          expiry_date: string
          extracted_data: Json | null
          front_image_path: string | null
          id: string
          issue_date: string
          issuing_country: string
          issuing_region: string | null
          license_class: Database['public']['Enums']['license_class'][]
          license_number: string | null
          profile_id: string
          provider_reference: string | null
          provider_response: Json | null
          provider_session_id: string | null
          provider_status: string | null
          rejection_reason: string | null
          status: Database['public']['Enums']['verification_status']
          updated_at: string
          verification_provider: string | null
          verified_at: string | null
          verified_by: string | null
        }
        Insert: {
          back_image_path?: string | null
          canonical_status?: string | null
          confidence?: number | null
          created_at?: string
          expiry_date: string
          extracted_data?: Json | null
          front_image_path?: string | null
          id?: string
          issue_date: string
          issuing_country: string
          issuing_region?: string | null
          license_class: Database['public']['Enums']['license_class'][]
          license_number?: string | null
          profile_id: string
          provider_reference?: string | null
          provider_response?: Json | null
          provider_session_id?: string | null
          provider_status?: string | null
          rejection_reason?: string | null
          status?: Database['public']['Enums']['verification_status']
          updated_at?: string
          verification_provider?: string | null
          verified_at?: string | null
          verified_by?: string | null
        }
        Update: {
          back_image_path?: string | null
          canonical_status?: string | null
          confidence?: number | null
          created_at?: string
          expiry_date?: string
          extracted_data?: Json | null
          front_image_path?: string | null
          id?: string
          issue_date?: string
          issuing_country?: string
          issuing_region?: string | null
          license_class?: Database['public']['Enums']['license_class'][]
          license_number?: string | null
          profile_id?: string
          provider_reference?: string | null
          provider_response?: Json | null
          provider_session_id?: string | null
          provider_status?: string | null
          rejection_reason?: string | null
          status?: Database['public']['Enums']['verification_status']
          updated_at?: string
          verification_provider?: string | null
          verified_at?: string | null
          verified_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'driver_licenses_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'driver_licenses_verified_by_fkey'
            columns: ['verified_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      dte_documents: {
        Row: {
          created_at: string
          dte_number: string
          dte_type: Database['public']['Enums']['dte_type']
          id: string
          issued_at: string | null
          issuer_name: string
          issuer_tax_id: string
          json_url: string | null
          payout_id: string | null
          pdf_url: string | null
          provider: string
          provider_document_id: string | null
          provider_response: Json | null
          recipient_name: string
          recipient_tax_id: string | null
          rental_id: string | null
          sent_at: string | null
          status: Database['public']['Enums']['dte_status']
          subtotal_cents: number
          tax_cents: number
          total_cents: number
          updated_at: string
        }
        Insert: {
          created_at?: string
          dte_number: string
          dte_type: Database['public']['Enums']['dte_type']
          id?: string
          issued_at?: string | null
          issuer_name: string
          issuer_tax_id: string
          json_url?: string | null
          payout_id?: string | null
          pdf_url?: string | null
          provider?: string
          provider_document_id?: string | null
          provider_response?: Json | null
          recipient_name: string
          recipient_tax_id?: string | null
          rental_id?: string | null
          sent_at?: string | null
          status?: Database['public']['Enums']['dte_status']
          subtotal_cents: number
          tax_cents: number
          total_cents: number
          updated_at?: string
        }
        Update: {
          created_at?: string
          dte_number?: string
          dte_type?: Database['public']['Enums']['dte_type']
          id?: string
          issued_at?: string | null
          issuer_name?: string
          issuer_tax_id?: string
          json_url?: string | null
          payout_id?: string | null
          pdf_url?: string | null
          provider?: string
          provider_document_id?: string | null
          provider_response?: Json | null
          recipient_name?: string
          recipient_tax_id?: string | null
          rental_id?: string | null
          sent_at?: string | null
          status?: Database['public']['Enums']['dte_status']
          subtotal_cents?: number
          tax_cents?: number
          total_cents?: number
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'dte_documents_payout_id_fkey'
            columns: ['payout_id']
            isOneToOne: false
            referencedRelation: 'payouts'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'dte_documents_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      erased_identity_hashes: {
        Row: {
          erased_at: string
          id: string
          identifier_sha256: string
          identifier_type: string
          profile_id: string
        }
        Insert: {
          erased_at?: string
          id?: string
          identifier_sha256: string
          identifier_type: string
          profile_id: string
        }
        Update: {
          erased_at?: string
          id?: string
          identifier_sha256?: string
          identifier_type?: string
          profile_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'erased_identity_hashes_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      favorites: {
        Row: {
          created_at: string
          id: string
          profile_id: string
          vehicle_id: string
        }
        Insert: {
          created_at?: string
          id?: string
          profile_id: string
          vehicle_id: string
        }
        Update: {
          created_at?: string
          id?: string
          profile_id?: string
          vehicle_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'favorites_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'favorites_vehicle_id_fkey'
            columns: ['vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
        ]
      }
      feature_flags: {
        Row: {
          country_ids: string[] | null
          created_at: string
          description: string | null
          id: string
          is_enabled: boolean
          key: string
          percentage: number | null
          updated_at: string
          user_ids: string[] | null
        }
        Insert: {
          country_ids?: string[] | null
          created_at?: string
          description?: string | null
          id?: string
          is_enabled?: boolean
          key: string
          percentage?: number | null
          updated_at?: string
          user_ids?: string[] | null
        }
        Update: {
          country_ids?: string[] | null
          created_at?: string
          description?: string | null
          id?: string
          is_enabled?: boolean
          key?: string
          percentage?: number | null
          updated_at?: string
          user_ids?: string[] | null
        }
        Relationships: []
      }
      identity_documents: {
        Row: {
          back_image_path: string | null
          canonical_status: string | null
          confidence: number | null
          created_at: string
          date_of_birth: string | null
          document_number: string | null
          document_type: Database['public']['Enums']['id_document_type']
          expiry_date: string | null
          extracted_data: Json | null
          face_match_score: number | null
          first_name: string | null
          front_image_path: string | null
          id: string
          issue_date: string | null
          issuing_country: string
          issuing_region: string | null
          last_name: string | null
          liveness_score: number | null
          profile_id: string
          provider_reference: string | null
          provider_response: Json | null
          provider_session_id: string | null
          provider_status: string | null
          rejection_reason: string | null
          selfie_image_path: string | null
          status: Database['public']['Enums']['verification_status']
          updated_at: string
          verification_provider: string | null
          verified_at: string | null
          verified_by: string | null
        }
        Insert: {
          back_image_path?: string | null
          canonical_status?: string | null
          confidence?: number | null
          created_at?: string
          date_of_birth?: string | null
          document_number?: string | null
          document_type: Database['public']['Enums']['id_document_type']
          expiry_date?: string | null
          extracted_data?: Json | null
          face_match_score?: number | null
          first_name?: string | null
          front_image_path?: string | null
          id?: string
          issue_date?: string | null
          issuing_country: string
          issuing_region?: string | null
          last_name?: string | null
          liveness_score?: number | null
          profile_id: string
          provider_reference?: string | null
          provider_response?: Json | null
          provider_session_id?: string | null
          provider_status?: string | null
          rejection_reason?: string | null
          selfie_image_path?: string | null
          status?: Database['public']['Enums']['verification_status']
          updated_at?: string
          verification_provider?: string | null
          verified_at?: string | null
          verified_by?: string | null
        }
        Update: {
          back_image_path?: string | null
          canonical_status?: string | null
          confidence?: number | null
          created_at?: string
          date_of_birth?: string | null
          document_number?: string | null
          document_type?: Database['public']['Enums']['id_document_type']
          expiry_date?: string | null
          extracted_data?: Json | null
          face_match_score?: number | null
          first_name?: string | null
          front_image_path?: string | null
          id?: string
          issue_date?: string | null
          issuing_country?: string
          issuing_region?: string | null
          last_name?: string | null
          liveness_score?: number | null
          profile_id?: string
          provider_reference?: string | null
          provider_response?: Json | null
          provider_session_id?: string | null
          provider_status?: string | null
          rejection_reason?: string | null
          selfie_image_path?: string | null
          status?: Database['public']['Enums']['verification_status']
          updated_at?: string
          verification_provider?: string | null
          verified_at?: string | null
          verified_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'identity_documents_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'identity_documents_verified_by_fkey'
            columns: ['verified_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      inspection_assurance_reviews: {
        Row: {
          completed_at: string | null
          created_at: string
          decided_at: string | null
          decided_by_link_id: string | null
          decided_by_name: string | null
          decision_comment: string | null
          due_at: string
          evidence_hash: string | null
          id: string
          inspection_id: string
          inspection_type: string
          last_error: string | null
          last_synced_at: string | null
          partner: string
          partner_request: Json
          partner_response: Json
          partner_submission_id: string | null
          photo_count: number
          rejection_reasons: string[]
          rental_id: string
          report_json: Json
          report_storage_path: string | null
          report_url: string | null
          result_summary: string | null
          status: string
          submitted_at: string | null
          updated_at: string
        }
        Insert: {
          completed_at?: string | null
          created_at?: string
          decided_at?: string | null
          decided_by_link_id?: string | null
          decided_by_name?: string | null
          decision_comment?: string | null
          due_at?: string
          evidence_hash?: string | null
          id?: string
          inspection_id: string
          inspection_type: string
          last_error?: string | null
          last_synced_at?: string | null
          partner?: string
          partner_request?: Json
          partner_response?: Json
          partner_submission_id?: string | null
          photo_count?: number
          rejection_reasons?: string[]
          rental_id: string
          report_json?: Json
          report_storage_path?: string | null
          report_url?: string | null
          result_summary?: string | null
          status?: string
          submitted_at?: string | null
          updated_at?: string
        }
        Update: {
          completed_at?: string | null
          created_at?: string
          decided_at?: string | null
          decided_by_link_id?: string | null
          decided_by_name?: string | null
          decision_comment?: string | null
          due_at?: string
          evidence_hash?: string | null
          id?: string
          inspection_id?: string
          inspection_type?: string
          last_error?: string | null
          last_synced_at?: string | null
          partner?: string
          partner_request?: Json
          partner_response?: Json
          partner_submission_id?: string | null
          photo_count?: number
          rejection_reasons?: string[]
          rental_id?: string
          report_json?: Json
          report_storage_path?: string | null
          report_url?: string | null
          result_summary?: string | null
          status?: string
          submitted_at?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'inspection_assurance_reviews_decided_by_link_id_fkey'
            columns: ['decided_by_link_id']
            isOneToOne: false
            referencedRelation: 'partner_review_links'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'inspection_assurance_reviews_inspection_id_fkey'
            columns: ['inspection_id']
            isOneToOne: true
            referencedRelation: 'rental_inspections'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'inspection_assurance_reviews_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      inspection_review_photo_notes: {
        Row: {
          comment: string | null
          created_at: string
          id: string
          link_id: string
          photo_id: string
          review_id: string
          updated_at: string
          verdict: string | null
        }
        Insert: {
          comment?: string | null
          created_at?: string
          id?: string
          link_id: string
          photo_id: string
          review_id: string
          updated_at?: string
          verdict?: string | null
        }
        Update: {
          comment?: string | null
          created_at?: string
          id?: string
          link_id?: string
          photo_id?: string
          review_id?: string
          updated_at?: string
          verdict?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'inspection_review_photo_notes_link_id_fkey'
            columns: ['link_id']
            isOneToOne: false
            referencedRelation: 'partner_review_links'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'inspection_review_photo_notes_photo_id_fkey'
            columns: ['photo_id']
            isOneToOne: false
            referencedRelation: 'rental_inspection_images'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'inspection_review_photo_notes_review_id_fkey'
            columns: ['review_id']
            isOneToOne: false
            referencedRelation: 'inspection_assurance_reviews'
            referencedColumns: ['id']
          },
        ]
      }
      insurance_claims: {
        Row: {
          approved_amount_cents: number | null
          claim_number: string
          claimed_amount_cents: number
          created_at: string
          damage_report_id: string
          decided_at: string | null
          id: string
          insurance_policy_id: string
          paid_at: string | null
          provider_claim_id: string | null
          provider_response: Json | null
          status: Database['public']['Enums']['claim_status']
          status_reason: string | null
          submitted_at: string | null
          updated_at: string
        }
        Insert: {
          approved_amount_cents?: number | null
          claim_number: string
          claimed_amount_cents: number
          created_at?: string
          damage_report_id: string
          decided_at?: string | null
          id?: string
          insurance_policy_id: string
          paid_at?: string | null
          provider_claim_id?: string | null
          provider_response?: Json | null
          status?: Database['public']['Enums']['claim_status']
          status_reason?: string | null
          submitted_at?: string | null
          updated_at?: string
        }
        Update: {
          approved_amount_cents?: number | null
          claim_number?: string
          claimed_amount_cents?: number
          created_at?: string
          damage_report_id?: string
          decided_at?: string | null
          id?: string
          insurance_policy_id?: string
          paid_at?: string | null
          provider_claim_id?: string | null
          provider_response?: Json | null
          status?: Database['public']['Enums']['claim_status']
          status_reason?: string | null
          submitted_at?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'insurance_claims_damage_report_id_fkey'
            columns: ['damage_report_id']
            isOneToOne: false
            referencedRelation: 'damage_reports'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'insurance_claims_insurance_policy_id_fkey'
            columns: ['insurance_policy_id']
            isOneToOne: false
            referencedRelation: 'insurance_policies'
            referencedColumns: ['id']
          },
        ]
      }
      insurance_policies: {
        Row: {
          cancelled_at: string | null
          certificate_url: string | null
          coverage_limit_cents: number
          coverage_type: string
          created_at: string
          deductible_cents: number
          effective_from: string
          effective_until: string
          external_policy_id: string | null
          id: string
          idempotency_key: string | null
          issuance_error: string | null
          issue_status: string
          issued_at: string | null
          package_code: string | null
          partner_request: Json
          partner_response: Json
          policy_number: string
          premium_cents: number
          provider: string
          reconciled_at: string | null
          reconciliation_month: string | null
          rental_id: string
          terms_url: string | null
        }
        Insert: {
          cancelled_at?: string | null
          certificate_url?: string | null
          coverage_limit_cents: number
          coverage_type: string
          created_at?: string
          deductible_cents: number
          effective_from: string
          effective_until: string
          external_policy_id?: string | null
          id?: string
          idempotency_key?: string | null
          issuance_error?: string | null
          issue_status?: string
          issued_at?: string | null
          package_code?: string | null
          partner_request?: Json
          partner_response?: Json
          policy_number: string
          premium_cents: number
          provider: string
          reconciled_at?: string | null
          reconciliation_month?: string | null
          rental_id: string
          terms_url?: string | null
        }
        Update: {
          cancelled_at?: string | null
          certificate_url?: string | null
          coverage_limit_cents?: number
          coverage_type?: string
          created_at?: string
          deductible_cents?: number
          effective_from?: string
          effective_until?: string
          external_policy_id?: string | null
          id?: string
          idempotency_key?: string | null
          issuance_error?: string | null
          issue_status?: string
          issued_at?: string | null
          package_code?: string | null
          partner_request?: Json
          partner_response?: Json
          policy_number?: string
          premium_cents?: number
          provider?: string
          reconciled_at?: string | null
          reconciliation_month?: string | null
          rental_id?: string
          terms_url?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'insurance_policies_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      ledger_entries: {
        Row: {
          amount_cents: number
          created_at: string
          credit_profile_id: string | null
          currency: string
          debit_profile_id: string | null
          description: string
          entry_type: Database['public']['Enums']['ledger_entry_type']
          id: string
          metadata: Json | null
          payment_id: string | null
          payout_id: string | null
          rental_id: string | null
          transaction_id: string | null
        }
        Insert: {
          amount_cents: number
          created_at?: string
          credit_profile_id?: string | null
          currency?: string
          debit_profile_id?: string | null
          description: string
          entry_type: Database['public']['Enums']['ledger_entry_type']
          id?: string
          metadata?: Json | null
          payment_id?: string | null
          payout_id?: string | null
          rental_id?: string | null
          transaction_id?: string | null
        }
        Update: {
          amount_cents?: number
          created_at?: string
          credit_profile_id?: string | null
          currency?: string
          debit_profile_id?: string | null
          description?: string
          entry_type?: Database['public']['Enums']['ledger_entry_type']
          id?: string
          metadata?: Json | null
          payment_id?: string | null
          payout_id?: string | null
          rental_id?: string | null
          transaction_id?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'ledger_entries_credit_profile_id_fkey'
            columns: ['credit_profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'ledger_entries_debit_profile_id_fkey'
            columns: ['debit_profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'ledger_entries_payment_id_fkey'
            columns: ['payment_id']
            isOneToOne: false
            referencedRelation: 'payments'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'ledger_entries_payout_id_fkey'
            columns: ['payout_id']
            isOneToOne: false
            referencedRelation: 'payouts'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'ledger_entries_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'ledger_entries_transaction_id_fkey'
            columns: ['transaction_id']
            isOneToOne: false
            referencedRelation: 'ledger_transactions'
            referencedColumns: ['id']
          },
        ]
      }
      ledger_transactions: {
        Row: {
          created_at: string
          description: string
          id: string
          is_balanced: boolean
          metadata: Json | null
          reference_id: string | null
          reference_type: string | null
          verified_at: string | null
        }
        Insert: {
          created_at?: string
          description: string
          id?: string
          is_balanced?: boolean
          metadata?: Json | null
          reference_id?: string | null
          reference_type?: string | null
          verified_at?: string | null
        }
        Update: {
          created_at?: string
          description?: string
          id?: string
          is_balanced?: boolean
          metadata?: Json | null
          reference_id?: string | null
          reference_type?: string | null
          verified_at?: string | null
        }
        Relationships: []
      }
      legal_acceptances: {
        Row: {
          accepted_at: string
          document_type: string
          document_url: string
          document_version: string
          id: string
          ip_address: unknown
          profile_id: string
          user_agent: string | null
        }
        Insert: {
          accepted_at?: string
          document_type: string
          document_url: string
          document_version: string
          id?: string
          ip_address?: unknown
          profile_id: string
          user_agent?: string | null
        }
        Update: {
          accepted_at?: string
          document_type?: string
          document_url?: string
          document_version?: string
          id?: string
          ip_address?: unknown
          profile_id?: string
          user_agent?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'legal_acceptances_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      message_reads: {
        Row: {
          conversation_id: string
          id: string
          last_read_at: string
          last_read_message_id: string | null
          profile_id: string
        }
        Insert: {
          conversation_id: string
          id?: string
          last_read_at?: string
          last_read_message_id?: string | null
          profile_id: string
        }
        Update: {
          conversation_id?: string
          id?: string
          last_read_at?: string
          last_read_message_id?: string | null
          profile_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'message_reads_conversation_id_fkey'
            columns: ['conversation_id']
            isOneToOne: false
            referencedRelation: 'conversations'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'message_reads_last_read_message_id_fkey'
            columns: ['last_read_message_id']
            isOneToOne: false
            referencedRelation: 'messages'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'message_reads_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      messages: {
        Row: {
          client_message_id: string | null
          content: string | null
          conversation_id: string
          created_at: string
          deleted_at: string | null
          detected_types: string[]
          edited_at: string | null
          id: string
          is_deleted: boolean
          is_edited: boolean
          location_lat: number | null
          location_lng: number | null
          media_blurhash: string | null
          media_url: string | null
          message_type: Database['public']['Enums']['message_type']
          reply_to_id: string | null
          risk_detected_at: string | null
          sender_id: string
        }
        Insert: {
          client_message_id?: string | null
          content?: string | null
          conversation_id: string
          created_at?: string
          deleted_at?: string | null
          detected_types?: string[]
          edited_at?: string | null
          id?: string
          is_deleted?: boolean
          is_edited?: boolean
          location_lat?: number | null
          location_lng?: number | null
          media_blurhash?: string | null
          media_url?: string | null
          message_type?: Database['public']['Enums']['message_type']
          reply_to_id?: string | null
          risk_detected_at?: string | null
          sender_id: string
        }
        Update: {
          client_message_id?: string | null
          content?: string | null
          conversation_id?: string
          created_at?: string
          deleted_at?: string | null
          detected_types?: string[]
          edited_at?: string | null
          id?: string
          is_deleted?: boolean
          is_edited?: boolean
          location_lat?: number | null
          location_lng?: number | null
          media_blurhash?: string | null
          media_url?: string | null
          message_type?: Database['public']['Enums']['message_type']
          reply_to_id?: string | null
          risk_detected_at?: string | null
          sender_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'messages_conversation_id_fkey'
            columns: ['conversation_id']
            isOneToOne: false
            referencedRelation: 'conversations'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'messages_sender_id_fkey'
            columns: ['sender_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'messages_reply_to_id_fkey'
            columns: ['reply_to_id']
            isOneToOne: false
            referencedRelation: 'messages'
            referencedColumns: ['id']
          },
        ]
      }
      multa_disputes: {
        Row: {
          created_at: string
          dispute_reason: string
          disputed_by: string
          evidence_urls: string[]
          id: string
          multa_id: string
          resolution: string | null
          resolved_at: string | null
          resolved_by: string | null
          status: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          dispute_reason: string
          disputed_by: string
          evidence_urls?: string[]
          id?: string
          multa_id: string
          resolution?: string | null
          resolved_at?: string | null
          resolved_by?: string | null
          status?: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          dispute_reason?: string
          disputed_by?: string
          evidence_urls?: string[]
          id?: string
          multa_id?: string
          resolution?: string | null
          resolved_at?: string | null
          resolved_by?: string | null
          status?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'multa_disputes_disputed_by_fkey'
            columns: ['disputed_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'multa_disputes_multa_id_fkey'
            columns: ['multa_id']
            isOneToOne: false
            referencedRelation: 'multas'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'multa_disputes_resolved_by_fkey'
            columns: ['resolved_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      multas: {
        Row: {
          created_at: string
          description: string | null
          due_date: string | null
          evidence_urls: string[]
          fine_amount_cents: number
          id: string
          late_fee_cents: number
          liability: Database['public']['Enums']['multa_liability']
          liability_determined_at: string | null
          liability_determined_by: string | null
          location_address: string | null
          location_point: unknown
          official_document_url: string | null
          paid_at: string | null
          paid_by: string | null
          payment_reference: string | null
          rental_id: string | null
          status: Database['public']['Enums']['multa_status']
          total_amount_cents: number
          updated_at: string
          vehicle_id: string
          violation_date: string
          violation_number: string
          violation_type: string
        }
        Insert: {
          created_at?: string
          description?: string | null
          due_date?: string | null
          evidence_urls?: string[]
          fine_amount_cents: number
          id?: string
          late_fee_cents?: number
          liability?: Database['public']['Enums']['multa_liability']
          liability_determined_at?: string | null
          liability_determined_by?: string | null
          location_address?: string | null
          location_point?: unknown
          official_document_url?: string | null
          paid_at?: string | null
          paid_by?: string | null
          payment_reference?: string | null
          rental_id?: string | null
          status?: Database['public']['Enums']['multa_status']
          total_amount_cents: number
          updated_at?: string
          vehicle_id: string
          violation_date: string
          violation_number: string
          violation_type: string
        }
        Update: {
          created_at?: string
          description?: string | null
          due_date?: string | null
          evidence_urls?: string[]
          fine_amount_cents?: number
          id?: string
          late_fee_cents?: number
          liability?: Database['public']['Enums']['multa_liability']
          liability_determined_at?: string | null
          liability_determined_by?: string | null
          location_address?: string | null
          location_point?: unknown
          official_document_url?: string | null
          paid_at?: string | null
          paid_by?: string | null
          payment_reference?: string | null
          rental_id?: string | null
          status?: Database['public']['Enums']['multa_status']
          total_amount_cents?: number
          updated_at?: string
          vehicle_id?: string
          violation_date?: string
          violation_number?: string
          violation_type?: string
        }
        Relationships: [
          {
            foreignKeyName: 'multas_liability_determined_by_fkey'
            columns: ['liability_determined_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'multas_paid_by_fkey'
            columns: ['paid_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'multas_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'multas_vehicle_id_fkey'
            columns: ['vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
        ]
      }
      municipalities: {
        Row: {
          created_at: string
          id: string
          is_active: boolean
          name: string
          region_id: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          id?: string
          is_active?: boolean
          name: string
          region_id: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          id?: string
          is_active?: boolean
          name?: string
          region_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'municipalities_region_id_fkey'
            columns: ['region_id']
            isOneToOne: false
            referencedRelation: 'regions'
            referencedColumns: ['id']
          },
        ]
      }
      notification_preferences: {
        Row: {
          email_enabled: boolean
          profile_id: string
          push_booking_updates: boolean
          push_enabled: boolean
          push_host_bookings: boolean
          push_host_cancellations: boolean
          push_host_payouts: boolean
          push_new_messages: boolean
          push_promotions: boolean
          push_trip_reminders: boolean
          sms_enabled: boolean
          updated_at: string
        }
        Insert: {
          email_enabled?: boolean
          profile_id: string
          push_booking_updates?: boolean
          push_enabled?: boolean
          push_host_bookings?: boolean
          push_host_cancellations?: boolean
          push_host_payouts?: boolean
          push_new_messages?: boolean
          push_promotions?: boolean
          push_trip_reminders?: boolean
          sms_enabled?: boolean
          updated_at?: string
        }
        Update: {
          email_enabled?: boolean
          profile_id?: string
          push_booking_updates?: boolean
          push_enabled?: boolean
          push_host_bookings?: boolean
          push_host_cancellations?: boolean
          push_host_payouts?: boolean
          push_new_messages?: boolean
          push_promotions?: boolean
          push_trip_reminders?: boolean
          sms_enabled?: boolean
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'notification_preferences_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: true
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      notifications: {
        Row: {
          body: string
          channel: Database['public']['Enums']['notification_channel']
          created_at: string
          data: Json | null
          id: string
          is_read: boolean
          profile_id: string
          read_at: string | null
          sent_at: string | null
          title: string
        }
        Insert: {
          body: string
          channel?: Database['public']['Enums']['notification_channel']
          created_at?: string
          data?: Json | null
          id?: string
          is_read?: boolean
          profile_id: string
          read_at?: string | null
          sent_at?: string | null
          title: string
        }
        Update: {
          body?: string
          channel?: Database['public']['Enums']['notification_channel']
          created_at?: string
          data?: Json | null
          id?: string
          is_read?: boolean
          profile_id?: string
          read_at?: string | null
          sent_at?: string | null
          title?: string
        }
        Relationships: [
          {
            foreignKeyName: 'notifications_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      organization_invites: {
        Row: {
          accepted_at: string | null
          accepted_by: string | null
          created_at: string
          email: string | null
          id: string
          invited_by: string | null
          organization_id: string
          phone: string | null
          role: Database['public']['Enums']['user_role']
          status: string
          updated_at: string
        }
        Insert: {
          accepted_at?: string | null
          accepted_by?: string | null
          created_at?: string
          email?: string | null
          id?: string
          invited_by?: string | null
          organization_id: string
          phone?: string | null
          role?: Database['public']['Enums']['user_role']
          status?: string
          updated_at?: string
        }
        Update: {
          accepted_at?: string | null
          accepted_by?: string | null
          created_at?: string
          email?: string | null
          id?: string
          invited_by?: string | null
          organization_id?: string
          phone?: string | null
          role?: Database['public']['Enums']['user_role']
          status?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'organization_invites_accepted_by_fkey'
            columns: ['accepted_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'organization_invites_invited_by_fkey'
            columns: ['invited_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'organization_invites_organization_id_fkey'
            columns: ['organization_id']
            isOneToOne: false
            referencedRelation: 'organizations'
            referencedColumns: ['id']
          },
        ]
      }
      organization_members: {
        Row: {
          accepted_at: string | null
          created_at: string
          id: string
          invited_at: string
          invited_by: string | null
          is_active: boolean
          organization_id: string
          permissions: string[]
          profile_id: string
          role: Database['public']['Enums']['user_role']
          updated_at: string
        }
        Insert: {
          accepted_at?: string | null
          created_at?: string
          id?: string
          invited_at?: string
          invited_by?: string | null
          is_active?: boolean
          organization_id: string
          permissions?: string[]
          profile_id: string
          role?: Database['public']['Enums']['user_role']
          updated_at?: string
        }
        Update: {
          accepted_at?: string | null
          created_at?: string
          id?: string
          invited_at?: string
          invited_by?: string | null
          is_active?: boolean
          organization_id?: string
          permissions?: string[]
          profile_id?: string
          role?: Database['public']['Enums']['user_role']
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'organization_members_invited_by_fkey'
            columns: ['invited_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'organization_members_organization_id_fkey'
            columns: ['organization_id']
            isOneToOne: false
            referencedRelation: 'organizations'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'organization_members_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      organizations: {
        Row: {
          address_city: string | null
          address_line1: string | null
          address_line2: string | null
          address_postal_code: string | null
          address_region: string | null
          allows_individual_hosts: boolean
          city_id: string | null
          country_id: string
          created_at: string
          email: string
          id: string
          insurance_model: Database['public']['Enums']['insurance_model']
          is_active: boolean
          is_verified: boolean
          legal_name: string | null
          logo_blurhash: string | null
          logo_url: string | null
          marketplace_visibility_bypass: boolean
          name: string
          phone: string | null
          tax_id: string | null
          updated_at: string
          verified_at: string | null
          website: string | null
        }
        Insert: {
          address_city?: string | null
          address_line1?: string | null
          address_line2?: string | null
          address_postal_code?: string | null
          address_region?: string | null
          allows_individual_hosts?: boolean
          city_id?: string | null
          country_id: string
          created_at?: string
          email: string
          id?: string
          insurance_model?: Database['public']['Enums']['insurance_model']
          is_active?: boolean
          is_verified?: boolean
          legal_name?: string | null
          logo_blurhash?: string | null
          logo_url?: string | null
          marketplace_visibility_bypass?: boolean
          name: string
          phone?: string | null
          tax_id?: string | null
          updated_at?: string
          verified_at?: string | null
          website?: string | null
        }
        Update: {
          address_city?: string | null
          address_line1?: string | null
          address_line2?: string | null
          address_postal_code?: string | null
          address_region?: string | null
          allows_individual_hosts?: boolean
          city_id?: string | null
          country_id?: string
          created_at?: string
          email?: string
          id?: string
          insurance_model?: Database['public']['Enums']['insurance_model']
          is_active?: boolean
          is_verified?: boolean
          legal_name?: string | null
          logo_blurhash?: string | null
          logo_url?: string | null
          marketplace_visibility_bypass?: boolean
          name?: string
          phone?: string | null
          tax_id?: string | null
          updated_at?: string
          verified_at?: string | null
          website?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'organizations_city_id_fkey'
            columns: ['city_id']
            isOneToOne: false
            referencedRelation: 'cities'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'organizations_country_id_fkey'
            columns: ['country_id']
            isOneToOne: false
            referencedRelation: 'countries'
            referencedColumns: ['id']
          },
        ]
      }
      partner_review_events: {
        Row: {
          created_at: string
          detail: Json
          event_type: string
          id: string
          ip: unknown
          link_id: string
          photo_id: string | null
          session_id: string | null
          user_agent: string | null
        }
        Insert: {
          created_at?: string
          detail?: Json
          event_type: string
          id?: string
          ip?: unknown
          link_id: string
          photo_id?: string | null
          session_id?: string | null
          user_agent?: string | null
        }
        Update: {
          created_at?: string
          detail?: Json
          event_type?: string
          id?: string
          ip?: unknown
          link_id?: string
          photo_id?: string | null
          session_id?: string | null
          user_agent?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'partner_review_events_link_id_fkey'
            columns: ['link_id']
            isOneToOne: false
            referencedRelation: 'partner_review_links'
            referencedColumns: ['id']
          },
        ]
      }
      partner_review_links: {
        Row: {
          created_at: string
          expires_at: string
          id: string
          reviewer_id: string
          revoked_at: string | null
          subject_id: string
          subject_type: string
          token_hash: string
        }
        Insert: {
          created_at?: string
          expires_at: string
          id?: string
          reviewer_id: string
          revoked_at?: string | null
          subject_id: string
          subject_type: string
          token_hash: string
        }
        Update: {
          created_at?: string
          expires_at?: string
          id?: string
          reviewer_id?: string
          revoked_at?: string | null
          subject_id?: string
          subject_type?: string
          token_hash?: string
        }
        Relationships: [
          {
            foreignKeyName: 'partner_review_links_reviewer_id_fkey'
            columns: ['reviewer_id']
            isOneToOne: false
            referencedRelation: 'partner_reviewers'
            referencedColumns: ['id']
          },
        ]
      }
      partner_review_otps: {
        Row: {
          attempts: number
          code_hash: string
          consumed_at: string | null
          created_at: string
          expires_at: string
          id: string
          session_id: string
        }
        Insert: {
          attempts?: number
          code_hash: string
          consumed_at?: string | null
          created_at?: string
          expires_at: string
          id?: string
          session_id: string
        }
        Update: {
          attempts?: number
          code_hash?: string
          consumed_at?: string | null
          created_at?: string
          expires_at?: string
          id?: string
          session_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'partner_review_otps_session_id_fkey'
            columns: ['session_id']
            isOneToOne: false
            referencedRelation: 'partner_review_sessions'
            referencedColumns: ['id']
          },
        ]
      }
      partner_review_sessions: {
        Row: {
          confirmed_name: string | null
          cookie_hash: string
          created_at: string
          expires_at: string
          id: string
          ip: unknown
          link_id: string
          revoked_at: string | null
          user_agent: string | null
        }
        Insert: {
          confirmed_name?: string | null
          cookie_hash: string
          created_at?: string
          expires_at: string
          id?: string
          ip?: unknown
          link_id: string
          revoked_at?: string | null
          user_agent?: string | null
        }
        Update: {
          confirmed_name?: string | null
          cookie_hash?: string
          created_at?: string
          expires_at?: string
          id?: string
          ip?: unknown
          link_id?: string
          revoked_at?: string | null
          user_agent?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'partner_review_sessions_link_id_fkey'
            columns: ['link_id']
            isOneToOne: false
            referencedRelation: 'partner_review_links'
            referencedColumns: ['id']
          },
        ]
      }
      partner_reviewers: {
        Row: {
          created_at: string
          email: string
          full_name: string
          id: string
          organization: string
          revoked_at: string | null
          updated_at: string
        }
        Insert: {
          created_at?: string
          email: string
          full_name: string
          id?: string
          organization: string
          revoked_at?: string | null
          updated_at?: string
        }
        Update: {
          created_at?: string
          email?: string
          full_name?: string
          id?: string
          organization?: string
          revoked_at?: string | null
          updated_at?: string
        }
        Relationships: []
      }
      payment_method_tokens: {
        Row: {
          created_at: string
          payment_method_id: string
          provider: string
          provider_token: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          payment_method_id: string
          provider: string
          provider_token: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          payment_method_id?: string
          provider?: string
          provider_token?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'payment_method_tokens_payment_method_id_fkey'
            columns: ['payment_method_id']
            isOneToOne: true
            referencedRelation: 'payment_methods'
            referencedColumns: ['id']
          },
        ]
      }
      payment_methods: {
        Row: {
          billing_country: string | null
          billing_name: string | null
          card_brand: string | null
          card_exp_month: number | null
          card_exp_year: number | null
          card_fingerprint: string | null
          card_last4: string | null
          created_at: string
          deactivated_at: string | null
          id: string
          is_active: boolean
          is_default: boolean
          label: string | null
          method_type: Database['public']['Enums']['payment_method_type']
          metadata: Json
          profile_id: string
          provider: string
          provider_token: string
          updated_at: string
        }
        Insert: {
          billing_country?: string | null
          billing_name?: string | null
          card_brand?: string | null
          card_exp_month?: number | null
          card_exp_year?: number | null
          card_fingerprint?: string | null
          card_last4?: string | null
          created_at?: string
          deactivated_at?: string | null
          id?: string
          is_active?: boolean
          is_default?: boolean
          label?: string | null
          method_type: Database['public']['Enums']['payment_method_type']
          metadata?: Json
          profile_id: string
          provider: string
          provider_token?: string
          updated_at?: string
        }
        Update: {
          billing_country?: string | null
          billing_name?: string | null
          card_brand?: string | null
          card_exp_month?: number | null
          card_exp_year?: number | null
          card_fingerprint?: string | null
          card_last4?: string | null
          created_at?: string
          deactivated_at?: string | null
          id?: string
          is_active?: boolean
          is_default?: boolean
          label?: string | null
          method_type?: Database['public']['Enums']['payment_method_type']
          metadata?: Json
          profile_id?: string
          provider?: string
          provider_token?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'payment_methods_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      payments: {
        Row: {
          amount_cents: number
          authorized_at: string | null
          captured_at: string | null
          created_at: string
          currency: string
          failed_at: string | null
          id: string
          idempotency_key: string
          payment_method_id: string | null
          payment_url: string | null
          provider: string
          provider_payment_id: string | null
          provider_response: Json | null
          rental_id: string
          status: Database['public']['Enums']['payment_status']
          status_reason: string | null
          updated_at: string
        }
        Insert: {
          amount_cents: number
          authorized_at?: string | null
          captured_at?: string | null
          created_at?: string
          currency?: string
          failed_at?: string | null
          id?: string
          idempotency_key: string
          payment_method_id?: string | null
          payment_url?: string | null
          provider?: string
          provider_payment_id?: string | null
          provider_response?: Json | null
          rental_id: string
          status?: Database['public']['Enums']['payment_status']
          status_reason?: string | null
          updated_at?: string
        }
        Update: {
          amount_cents?: number
          authorized_at?: string | null
          captured_at?: string | null
          created_at?: string
          currency?: string
          failed_at?: string | null
          id?: string
          idempotency_key?: string
          payment_method_id?: string | null
          payment_url?: string | null
          provider?: string
          provider_payment_id?: string | null
          provider_response?: Json | null
          rental_id?: string
          status?: Database['public']['Enums']['payment_status']
          status_reason?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'payments_payment_method_id_fkey'
            columns: ['payment_method_id']
            isOneToOne: false
            referencedRelation: 'payment_methods'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'payments_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      payout_accounts: {
        Row: {
          account_holder_name: string | null
          account_number_full: string | null
          account_number_masked: string | null
          account_type: string
          bank_code: string | null
          bank_name: string | null
          country_code: string
          created_at: string
          currency: string
          id: string
          is_default: boolean
          is_verified: boolean
          organization_id: string | null
          profile_id: string | null
          provider: string
          provider_account_id: string | null
          provider_account_number_full: string | null
          updated_at: string
          verified_at: string | null
        }
        Insert: {
          account_holder_name?: string | null
          account_number_full?: string | null
          account_number_masked?: string | null
          account_type: string
          bank_code?: string | null
          bank_name?: string | null
          country_code: string
          created_at?: string
          currency?: string
          id?: string
          is_default?: boolean
          is_verified?: boolean
          organization_id?: string | null
          profile_id?: string | null
          provider?: string
          provider_account_id?: string | null
          provider_account_number_full?: string | null
          updated_at?: string
          verified_at?: string | null
        }
        Update: {
          account_holder_name?: string | null
          account_number_full?: string | null
          account_number_masked?: string | null
          account_type?: string
          bank_code?: string | null
          bank_name?: string | null
          country_code?: string
          created_at?: string
          currency?: string
          id?: string
          is_default?: boolean
          is_verified?: boolean
          organization_id?: string | null
          profile_id?: string | null
          provider?: string
          provider_account_id?: string | null
          provider_account_number_full?: string | null
          updated_at?: string
          verified_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'payout_accounts_organization_id_fkey'
            columns: ['organization_id']
            isOneToOne: false
            referencedRelation: 'organizations'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'payout_accounts_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      payout_items: {
        Row: {
          amount_cents: number
          created_at: string
          description: string
          id: string
          payout_id: string
          rental_id: string | null
        }
        Insert: {
          amount_cents: number
          created_at?: string
          description: string
          id?: string
          payout_id: string
          rental_id?: string | null
        }
        Update: {
          amount_cents?: number
          created_at?: string
          description?: string
          id?: string
          payout_id?: string
          rental_id?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'payout_items_payout_id_fkey'
            columns: ['payout_id']
            isOneToOne: false
            referencedRelation: 'payouts'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'payout_items_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      payouts: {
        Row: {
          amount_cents: number
          completed_at: string | null
          created_at: string
          currency: string
          failed_at: string | null
          id: string
          idempotency_key: string
          initiated_at: string
          organization_id: string | null
          payout_account_id: string
          profile_id: string
          provider: string
          provider_payout_id: string | null
          provider_response: Json | null
          status: Database['public']['Enums']['payout_status']
          status_reason: string | null
          updated_at: string
        }
        Insert: {
          amount_cents: number
          completed_at?: string | null
          created_at?: string
          currency?: string
          failed_at?: string | null
          id?: string
          idempotency_key: string
          initiated_at?: string
          organization_id?: string | null
          payout_account_id: string
          profile_id: string
          provider?: string
          provider_payout_id?: string | null
          provider_response?: Json | null
          status?: Database['public']['Enums']['payout_status']
          status_reason?: string | null
          updated_at?: string
        }
        Update: {
          amount_cents?: number
          completed_at?: string | null
          created_at?: string
          currency?: string
          failed_at?: string | null
          id?: string
          idempotency_key?: string
          initiated_at?: string
          organization_id?: string | null
          payout_account_id?: string
          profile_id?: string
          provider?: string
          provider_payout_id?: string | null
          provider_response?: Json | null
          status?: Database['public']['Enums']['payout_status']
          status_reason?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'payouts_organization_id_fkey'
            columns: ['organization_id']
            isOneToOne: false
            referencedRelation: 'organizations'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'payouts_payout_account_id_fkey'
            columns: ['payout_account_id']
            isOneToOne: false
            referencedRelation: 'payout_accounts'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'payouts_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      phone_manual_activations: {
        Row: {
          approved_by: string
          created_at: string
          id: string
          phone: string
          reason: string
          user_id: string | null
        }
        Insert: {
          approved_by: string
          created_at?: string
          id?: string
          phone: string
          reason: string
          user_id?: string | null
        }
        Update: {
          approved_by?: string
          created_at?: string
          id?: string
          phone?: string
          reason?: string
          user_id?: string | null
        }
        Relationships: []
      }
      phone_verifications: {
        Row: {
          attempts: number
          created_at: string
          expires_at: string
          id: string
          phone: string
          provider_reference: string | null
          sent_at: string
          status: string
          token_consumed_at: string | null
          token_hash: string | null
          updated_at: string
          user_id: string | null
          validated_at: string | null
        }
        Insert: {
          attempts?: number
          created_at?: string
          expires_at: string
          id?: string
          phone: string
          provider_reference?: string | null
          sent_at?: string
          status?: string
          token_consumed_at?: string | null
          token_hash?: string | null
          updated_at?: string
          user_id?: string | null
          validated_at?: string | null
        }
        Update: {
          attempts?: number
          created_at?: string
          expires_at?: string
          id?: string
          phone?: string
          provider_reference?: string | null
          sent_at?: string
          status?: string
          token_consumed_at?: string | null
          token_hash?: string | null
          updated_at?: string
          user_id?: string | null
          validated_at?: string | null
        }
        Relationships: []
      }
      platform_config: {
        Row: {
          cancel_fee_percent: number
          country_id: string
          coverage_assistance_phone_primary: string
          coverage_assistance_phone_secondary: string
          coverage_status: string
          created_at: string
          default_deposit_percent: number
          deposit_hold_days: number
          free_cancel_hours: number
          id: string
          insurance_api_endpoint: string | null
          insurance_partner_id: string | null
          max_booking_lead_days: number
          max_deposit_cents: number
          max_rental_days: number
          min_booking_lead_hours: number
          min_guest_age: number
          min_host_age: number
          min_license_years: number
          min_rental_hours: number
          updated_at: string
        }
        Insert: {
          cancel_fee_percent?: number
          country_id: string
          coverage_assistance_phone_primary?: string
          coverage_assistance_phone_secondary?: string
          coverage_status?: string
          created_at?: string
          default_deposit_percent?: number
          deposit_hold_days?: number
          free_cancel_hours?: number
          id?: string
          insurance_api_endpoint?: string | null
          insurance_partner_id?: string | null
          max_booking_lead_days?: number
          max_deposit_cents?: number
          max_rental_days?: number
          min_booking_lead_hours?: number
          min_guest_age?: number
          min_host_age?: number
          min_license_years?: number
          min_rental_hours?: number
          updated_at?: string
        }
        Update: {
          cancel_fee_percent?: number
          country_id?: string
          coverage_assistance_phone_primary?: string
          coverage_assistance_phone_secondary?: string
          coverage_status?: string
          created_at?: string
          default_deposit_percent?: number
          deposit_hold_days?: number
          free_cancel_hours?: number
          id?: string
          insurance_api_endpoint?: string | null
          insurance_partner_id?: string | null
          max_booking_lead_days?: number
          max_deposit_cents?: number
          max_rental_days?: number
          min_booking_lead_hours?: number
          min_guest_age?: number
          min_host_age?: number
          min_license_years?: number
          min_rental_hours?: number
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'platform_config_country_id_fkey'
            columns: ['country_id']
            isOneToOne: true
            referencedRelation: 'countries'
            referencedColumns: ['id']
          },
        ]
      }
      product_analytics_events: {
        Row: {
          app_version: string | null
          attributed_at: string | null
          attribution_source: string | null
          client_event_id: string
          country_code: string | null
          created_at: string
          device_type: string | null
          duration_ms: number | null
          element_id: string | null
          element_type: string | null
          event_name: string
          event_type: string
          id: string
          installation_fingerprint: string | null
          ip_fingerprint: string | null
          locale: string | null
          metadata: Json
          occurred_at: string
          platform: string | null
          profile_id: string | null
          received_at: string
          route_path: string | null
          screen_name: string | null
          scroll_depth_pct: number | null
          session_id: string
          timezone: string | null
          user_agent_fingerprint: string | null
        }
        Insert: {
          app_version?: string | null
          attributed_at?: string | null
          attribution_source?: string | null
          client_event_id: string
          country_code?: string | null
          created_at?: string
          device_type?: string | null
          duration_ms?: number | null
          element_id?: string | null
          element_type?: string | null
          event_name: string
          event_type: string
          id?: string
          installation_fingerprint?: string | null
          ip_fingerprint?: string | null
          locale?: string | null
          metadata?: Json
          occurred_at?: string
          platform?: string | null
          profile_id?: string | null
          received_at?: string
          route_path?: string | null
          screen_name?: string | null
          scroll_depth_pct?: number | null
          session_id: string
          timezone?: string | null
          user_agent_fingerprint?: string | null
        }
        Update: {
          app_version?: string | null
          attributed_at?: string | null
          attribution_source?: string | null
          client_event_id?: string
          country_code?: string | null
          created_at?: string
          device_type?: string | null
          duration_ms?: number | null
          element_id?: string | null
          element_type?: string | null
          event_name?: string
          event_type?: string
          id?: string
          installation_fingerprint?: string | null
          ip_fingerprint?: string | null
          locale?: string | null
          metadata?: Json
          occurred_at?: string
          platform?: string | null
          profile_id?: string | null
          received_at?: string
          route_path?: string | null
          screen_name?: string | null
          scroll_depth_pct?: number | null
          session_id?: string
          timezone?: string | null
          user_agent_fingerprint?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'product_analytics_events_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      profiles: {
        Row: {
          avatar_blurhash: string | null
          avatar_url: string | null
          city_id: string | null
          country_id: string | null
          created_at: string
          data_blocked_at: string | null
          date_of_birth: string | null
          delete_requested_at: string | null
          delete_scheduled_at: string | null
          deleted_at: string | null
          deletion_reason: string | null
          display_name: string | null
          email: string
          first_name: string | null
          guest_rating_avg: number | null
          guest_rating_count: number
          host_rating_avg: number | null
          host_rating_count: number
          id: string
          is_host_enabled: boolean
          is_suspended: boolean
          is_verified: boolean
          last_active_at: string | null
          last_name: string | null
          marketplace_visibility_bypass: boolean
          phone: string | null
          preferred_currency: string | null
          preferred_locale: string | null
          roles: Database['public']['Enums']['user_role'][]
          suspended_reason: string | null
          suspended_until: string | null
          updated_at: string
        }
        Insert: {
          avatar_blurhash?: string | null
          avatar_url?: string | null
          city_id?: string | null
          country_id?: string | null
          created_at?: string
          data_blocked_at?: string | null
          date_of_birth?: string | null
          delete_requested_at?: string | null
          delete_scheduled_at?: string | null
          deleted_at?: string | null
          deletion_reason?: string | null
          display_name?: string | null
          email: string
          first_name?: string | null
          guest_rating_avg?: number | null
          guest_rating_count?: number
          host_rating_avg?: number | null
          host_rating_count?: number
          id: string
          is_host_enabled?: boolean
          is_suspended?: boolean
          is_verified?: boolean
          last_active_at?: string | null
          last_name?: string | null
          marketplace_visibility_bypass?: boolean
          phone?: string | null
          preferred_currency?: string | null
          preferred_locale?: string | null
          roles?: Database['public']['Enums']['user_role'][]
          suspended_reason?: string | null
          suspended_until?: string | null
          updated_at?: string
        }
        Update: {
          avatar_blurhash?: string | null
          avatar_url?: string | null
          city_id?: string | null
          country_id?: string | null
          created_at?: string
          data_blocked_at?: string | null
          date_of_birth?: string | null
          delete_requested_at?: string | null
          delete_scheduled_at?: string | null
          deleted_at?: string | null
          deletion_reason?: string | null
          display_name?: string | null
          email?: string
          first_name?: string | null
          guest_rating_avg?: number | null
          guest_rating_count?: number
          host_rating_avg?: number | null
          host_rating_count?: number
          id?: string
          is_host_enabled?: boolean
          is_suspended?: boolean
          is_verified?: boolean
          last_active_at?: string | null
          last_name?: string | null
          marketplace_visibility_bypass?: boolean
          phone?: string | null
          preferred_currency?: string | null
          preferred_locale?: string | null
          roles?: Database['public']['Enums']['user_role'][]
          suspended_reason?: string | null
          suspended_until?: string | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'profiles_city_id_fkey'
            columns: ['city_id']
            isOneToOne: false
            referencedRelation: 'cities'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'profiles_country_id_fkey'
            columns: ['country_id']
            isOneToOne: false
            referencedRelation: 'countries'
            referencedColumns: ['id']
          },
        ]
      }
      promo_code_uses: {
        Row: {
          created_at: string
          discount_cents: number
          id: string
          profile_id: string
          promo_code_id: string
          rental_id: string
        }
        Insert: {
          created_at?: string
          discount_cents: number
          id?: string
          profile_id: string
          promo_code_id: string
          rental_id: string
        }
        Update: {
          created_at?: string
          discount_cents?: number
          id?: string
          profile_id?: string
          promo_code_id?: string
          rental_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'promo_code_uses_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'promo_code_uses_promo_code_id_fkey'
            columns: ['promo_code_id']
            isOneToOne: false
            referencedRelation: 'promo_codes'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'promo_code_uses_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      promo_codes: {
        Row: {
          code: string
          country_ids: string[] | null
          created_at: string
          description: string | null
          discount_type: string
          discount_value: number
          first_rental_only: boolean
          id: string
          is_active: boolean
          max_discount_cents: number | null
          max_uses: number | null
          max_uses_per_user: number | null
          min_rental_cents: number | null
          updated_at: string
          use_count: number
          user_ids: string[] | null
          valid_from: string
          valid_until: string | null
        }
        Insert: {
          code: string
          country_ids?: string[] | null
          created_at?: string
          description?: string | null
          discount_type: string
          discount_value: number
          first_rental_only?: boolean
          id?: string
          is_active?: boolean
          max_discount_cents?: number | null
          max_uses?: number | null
          max_uses_per_user?: number | null
          min_rental_cents?: number | null
          updated_at?: string
          use_count?: number
          user_ids?: string[] | null
          valid_from?: string
          valid_until?: string | null
        }
        Update: {
          code?: string
          country_ids?: string[] | null
          created_at?: string
          description?: string | null
          discount_type?: string
          discount_value?: number
          first_rental_only?: boolean
          id?: string
          is_active?: boolean
          max_discount_cents?: number | null
          max_uses?: number | null
          max_uses_per_user?: number | null
          min_rental_cents?: number | null
          updated_at?: string
          use_count?: number
          user_ids?: string[] | null
          valid_from?: string
          valid_until?: string | null
        }
        Relationships: []
      }
      push_tokens: {
        Row: {
          created_at: string
          device_id: string | null
          device_name: string | null
          device_type: string | null
          failure_count: number
          id: string
          is_active: boolean
          last_failure_at: string | null
          last_failure_reason: string | null
          last_used_at: string | null
          profile_id: string
          provider: string
          token: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          device_id?: string | null
          device_name?: string | null
          device_type?: string | null
          failure_count?: number
          id?: string
          is_active?: boolean
          last_failure_at?: string | null
          last_failure_reason?: string | null
          last_used_at?: string | null
          profile_id: string
          provider: string
          token: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          device_id?: string | null
          device_name?: string | null
          device_type?: string | null
          failure_count?: number
          id?: string
          is_active?: boolean
          last_failure_at?: string | null
          last_failure_reason?: string | null
          last_used_at?: string | null
          profile_id?: string
          provider?: string
          token?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'push_tokens_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      rate_limit_attempts: {
        Row: {
          action: string
          attempts: number
          block_reason: string | null
          blocked_at: string | null
          blocked_until: string | null
          created_at: string
          first_attempt_at: string
          id: string
          identifier: string
          identifier_type: string
          is_blocked: boolean
          last_attempt_at: string
          window_size_seconds: number
          window_start: string
        }
        Insert: {
          action: string
          attempts?: number
          block_reason?: string | null
          blocked_at?: string | null
          blocked_until?: string | null
          created_at?: string
          first_attempt_at?: string
          id?: string
          identifier: string
          identifier_type: string
          is_blocked?: boolean
          last_attempt_at?: string
          window_size_seconds?: number
          window_start?: string
        }
        Update: {
          action?: string
          attempts?: number
          block_reason?: string | null
          blocked_at?: string | null
          blocked_until?: string | null
          created_at?: string
          first_attempt_at?: string
          id?: string
          identifier?: string
          identifier_type?: string
          is_blocked?: boolean
          last_attempt_at?: string
          window_size_seconds?: number
          window_start?: string
        }
        Relationships: []
      }
      regions: {
        Row: {
          code: string
          country_id: string
          created_at: string
          id: string
          is_active: boolean
          name: string
          updated_at: string
        }
        Insert: {
          code: string
          country_id: string
          created_at?: string
          id?: string
          is_active?: boolean
          name: string
          updated_at?: string
        }
        Update: {
          code?: string
          country_id?: string
          created_at?: string
          id?: string
          is_active?: boolean
          name?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'regions_country_id_fkey'
            columns: ['country_id']
            isOneToOne: false
            referencedRelation: 'countries'
            referencedColumns: ['id']
          },
        ]
      }
      rental_disputes: {
        Row: {
          category: Database['public']['Enums']['dispute_category']
          claim_amount_cents: number
          claim_description: string
          claim_evidence_metadata: Json
          claim_evidence_urls: string[]
          claimant_id: string
          created_at: string
          id: string
          rental_id: string
          resolution_notes: string | null
          resolved_amount_cents: number | null
          resolved_at: string | null
          resolved_by: string | null
          resolved_in_favor_of: string | null
          responded_at: string | null
          respondent_id: string
          response_deadline: string | null
          response_description: string | null
          response_evidence_metadata: Json
          response_evidence_urls: string[] | null
          status: Database['public']['Enums']['dispute_status']
          updated_at: string
        }
        Insert: {
          category: Database['public']['Enums']['dispute_category']
          claim_amount_cents?: number
          claim_description: string
          claim_evidence_metadata?: Json
          claim_evidence_urls?: string[]
          claimant_id: string
          created_at?: string
          id?: string
          rental_id: string
          resolution_notes?: string | null
          resolved_amount_cents?: number | null
          resolved_at?: string | null
          resolved_by?: string | null
          resolved_in_favor_of?: string | null
          responded_at?: string | null
          respondent_id: string
          response_deadline?: string | null
          response_description?: string | null
          response_evidence_metadata?: Json
          response_evidence_urls?: string[] | null
          status?: Database['public']['Enums']['dispute_status']
          updated_at?: string
        }
        Update: {
          category?: Database['public']['Enums']['dispute_category']
          claim_amount_cents?: number
          claim_description?: string
          claim_evidence_metadata?: Json
          claim_evidence_urls?: string[]
          claimant_id?: string
          created_at?: string
          id?: string
          rental_id?: string
          resolution_notes?: string | null
          resolved_amount_cents?: number | null
          resolved_at?: string | null
          resolved_by?: string | null
          resolved_in_favor_of?: string | null
          responded_at?: string | null
          respondent_id?: string
          response_deadline?: string | null
          response_description?: string | null
          response_evidence_metadata?: Json
          response_evidence_urls?: string[] | null
          status?: Database['public']['Enums']['dispute_status']
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'rental_disputes_claimant_id_fkey'
            columns: ['claimant_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rental_disputes_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rental_disputes_resolved_by_fkey'
            columns: ['resolved_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rental_disputes_resolved_in_favor_of_fkey'
            columns: ['resolved_in_favor_of']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rental_disputes_respondent_id_fkey'
            columns: ['respondent_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      rental_extensions: {
        Row: {
          additional_cents: number
          additional_hours: number
          approved_at: string | null
          approved_by: string | null
          created_at: string
          id: string
          new_end_at: string
          original_end_at: string
          rental_id: string
          requested_by: string
          status: string
          updated_at: string
        }
        Insert: {
          additional_cents: number
          additional_hours: number
          approved_at?: string | null
          approved_by?: string | null
          created_at?: string
          id?: string
          new_end_at: string
          original_end_at: string
          rental_id: string
          requested_by: string
          status?: string
          updated_at?: string
        }
        Update: {
          additional_cents?: number
          additional_hours?: number
          approved_at?: string | null
          approved_by?: string | null
          created_at?: string
          id?: string
          new_end_at?: string
          original_end_at?: string
          rental_id?: string
          requested_by?: string
          status?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'rental_extensions_approved_by_fkey'
            columns: ['approved_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rental_extensions_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rental_extensions_requested_by_fkey'
            columns: ['requested_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      rental_inspection_images: {
        Row: {
          area: string
          caption: string | null
          capture_source: string
          captured_at: string | null
          created_at: string
          id: string
          inspection_id: string
          is_damage: boolean
          latitude: number | null
          location_accuracy_meters: number | null
          longitude: number | null
          photo_sha256: string | null
          sequence_index: number | null
          storage_path: string
          url: string
          verification_payload: Json
        }
        Insert: {
          area: string
          caption?: string | null
          capture_source?: string
          captured_at?: string | null
          created_at?: string
          id?: string
          inspection_id: string
          is_damage?: boolean
          latitude?: number | null
          location_accuracy_meters?: number | null
          longitude?: number | null
          photo_sha256?: string | null
          sequence_index?: number | null
          storage_path: string
          url: string
          verification_payload?: Json
        }
        Update: {
          area?: string
          caption?: string | null
          capture_source?: string
          captured_at?: string | null
          created_at?: string
          id?: string
          inspection_id?: string
          is_damage?: boolean
          latitude?: number | null
          location_accuracy_meters?: number | null
          longitude?: number | null
          photo_sha256?: string | null
          sequence_index?: number | null
          storage_path?: string
          url?: string
          verification_payload?: Json
        }
        Relationships: [
          {
            foreignKeyName: 'rental_inspection_images_inspection_id_fkey'
            columns: ['inspection_id']
            isOneToOne: false
            referencedRelation: 'rental_inspections'
            referencedColumns: ['id']
          },
        ]
      }
      rental_inspections: {
        Row: {
          created_at: string
          damages: Json
          exterior_condition: string | null
          fuel_level: number
          guest_signature_url: string | null
          host_signature_url: string | null
          id: string
          inspection_type: string
          interior_condition: string | null
          notes: string | null
          odometer_km: number
          performed_at: string
          performed_by: string
          presence_token_id: string | null
          presence_verified_at: string | null
          presence_verifier_id: string | null
          rental_id: string
        }
        Insert: {
          created_at?: string
          damages?: Json
          exterior_condition?: string | null
          fuel_level: number
          guest_signature_url?: string | null
          host_signature_url?: string | null
          id?: string
          inspection_type: string
          interior_condition?: string | null
          notes?: string | null
          odometer_km: number
          performed_at?: string
          performed_by: string
          rental_id: string
        }
        Update: {
          created_at?: string
          damages?: Json
          exterior_condition?: string | null
          fuel_level?: number
          guest_signature_url?: string | null
          host_signature_url?: string | null
          id?: string
          inspection_type?: string
          interior_condition?: string | null
          notes?: string | null
          odometer_km?: number
          performed_at?: string
          performed_by?: string
          rental_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'rental_inspections_performed_by_fkey'
            columns: ['performed_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rental_inspections_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
        ]
      }
      rentals: {
        Row: {
          actual_end_at: string | null
          actual_start_at: string | null
          base_rate_cents: number
          blocked_until: string
          booking_code: string
          cancellation_fee_cents: number | null
          cancellation_reason: string | null
          cancelled_at: string | null
          cancelled_by: Database['public']['Enums']['cancellation_party'] | null
          cleaning_fee_cents: number
          created_at: string
          delivery_fee_cents: number
          deposit_claimed_cents: number
          deposit_held_cents: number
          deposit_released_cents: number
          discount_cents: number
          discount_code: string | null
          dropoff_address: string
          dropoff_fuel_level: number | null
          dropoff_location: unknown
          dropoff_odometer_km: number | null
          dte_attempts: number
          dte_generated_at: string | null
          dte_id: string | null
          dte_last_error: string | null
          dte_status: string | null
          dte_url: string | null
          duration_hours: number
          end_at: string
          guest_id: string
          host_id: string
          host_payout_cents: number
          host_response_deadline_at: string | null
          host_responded_at: string | null
          rental_terms_version: string | null
          id: string
          insurance_certificate_url: string | null
          insurance_fee_cents: number
          insurance_model: Database['public']['Enums']['insurance_model']
          insurance_policy_id: string | null
          is_same_location: boolean
          late_fee_cents: number
          organization_id: string | null
          pickup_address: string
          pickup_fuel_level: number | null
          pickup_location: unknown
          pickup_odometer_km: number | null
          platform_fee_cents: number
          payment_processing_fee_cents: number
          security_deposit_cents: number
          start_at: string
          status: Database['public']['Enums']['rental_status']
          status_reason: string | null
          subtotal_cents: number
          tax_cents: number
          timezone: string
          total_cents: number
          updated_at: string
          vehicle_id: string
          young_driver_fee_cents: number
        }
        Insert: {
          actual_end_at?: string | null
          actual_start_at?: string | null
          base_rate_cents: number
          blocked_until?: string
          booking_code: string
          cancellation_fee_cents?: number | null
          cancellation_reason?: string | null
          cancelled_at?: string | null
          cancelled_by?: Database['public']['Enums']['cancellation_party'] | null
          cleaning_fee_cents?: number
          created_at?: string
          delivery_fee_cents?: number
          deposit_claimed_cents?: number
          deposit_held_cents?: number
          deposit_released_cents?: number
          discount_cents?: number
          discount_code?: string | null
          dropoff_address: string
          dropoff_fuel_level?: number | null
          dropoff_location: unknown
          dropoff_odometer_km?: number | null
          dte_attempts?: number
          dte_generated_at?: string | null
          dte_id?: string | null
          dte_last_error?: string | null
          dte_status?: string | null
          dte_url?: string | null
          duration_hours: number
          end_at: string
          guest_id: string
          host_id: string
          host_payout_cents: number
          host_response_deadline_at?: string | null
          host_responded_at?: string | null
          rental_terms_version?: string | null
          id?: string
          insurance_certificate_url?: string | null
          insurance_fee_cents?: number
          insurance_model: Database['public']['Enums']['insurance_model']
          insurance_policy_id?: string | null
          is_same_location?: boolean
          late_fee_cents?: number
          organization_id?: string | null
          pickup_address: string
          pickup_fuel_level?: number | null
          pickup_location: unknown
          pickup_odometer_km?: number | null
          platform_fee_cents: number
          payment_processing_fee_cents?: number
          security_deposit_cents: number
          start_at: string
          status?: Database['public']['Enums']['rental_status']
          status_reason?: string | null
          subtotal_cents: number
          tax_cents?: number
          timezone: string
          total_cents: number
          updated_at?: string
          vehicle_id: string
          young_driver_fee_cents?: number
        }
        Update: {
          actual_end_at?: string | null
          actual_start_at?: string | null
          base_rate_cents?: number
          blocked_until?: string
          booking_code?: string
          cancellation_fee_cents?: number | null
          cancellation_reason?: string | null
          cancelled_at?: string | null
          cancelled_by?: Database['public']['Enums']['cancellation_party'] | null
          cleaning_fee_cents?: number
          created_at?: string
          delivery_fee_cents?: number
          deposit_claimed_cents?: number
          deposit_held_cents?: number
          deposit_released_cents?: number
          discount_cents?: number
          discount_code?: string | null
          dropoff_address?: string
          dropoff_fuel_level?: number | null
          dropoff_location?: unknown
          dropoff_odometer_km?: number | null
          dte_attempts?: number
          dte_generated_at?: string | null
          dte_id?: string | null
          dte_last_error?: string | null
          dte_status?: string | null
          dte_url?: string | null
          duration_hours?: number
          end_at?: string
          guest_id?: string
          host_id?: string
          host_payout_cents?: number
          host_response_deadline_at?: string | null
          host_responded_at?: string | null
          rental_terms_version?: string | null
          id?: string
          insurance_certificate_url?: string | null
          insurance_fee_cents?: number
          insurance_model?: Database['public']['Enums']['insurance_model']
          insurance_policy_id?: string | null
          is_same_location?: boolean
          late_fee_cents?: number
          organization_id?: string | null
          pickup_address?: string
          pickup_fuel_level?: number | null
          pickup_location?: unknown
          pickup_odometer_km?: number | null
          platform_fee_cents?: number
          security_deposit_cents?: number
          start_at?: string
          status?: Database['public']['Enums']['rental_status']
          status_reason?: string | null
          subtotal_cents?: number
          tax_cents?: number
          timezone?: string
          total_cents?: number
          updated_at?: string
          vehicle_id?: string
          young_driver_fee_cents?: number
        }
        Relationships: [
          {
            foreignKeyName: 'rentals_guest_id_fkey'
            columns: ['guest_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rentals_host_id_fkey'
            columns: ['host_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rentals_organization_id_fkey'
            columns: ['organization_id']
            isOneToOne: false
            referencedRelation: 'organizations'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'rentals_vehicle_id_fkey'
            columns: ['vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
        ]
      }
      reports: {
        Row: {
          action_taken: string | null
          assigned_to: string | null
          category: string
          created_at: string
          description: string
          evidence_urls: string[]
          id: string
          priority: string
          report_type: string
          reported_message_id: string | null
          reported_profile_id: string | null
          reported_review_id: string | null
          reported_vehicle_id: string | null
          reporter_id: string
          resolution_notes: string | null
          resolved_at: string | null
          reviewed_at: string | null
          status: string
          updated_at: string
        }
        Insert: {
          action_taken?: string | null
          assigned_to?: string | null
          category: string
          created_at?: string
          description: string
          evidence_urls?: string[]
          id?: string
          priority?: string
          report_type: string
          reported_message_id?: string | null
          reported_profile_id?: string | null
          reported_review_id?: string | null
          reported_vehicle_id?: string | null
          reporter_id: string
          resolution_notes?: string | null
          resolved_at?: string | null
          reviewed_at?: string | null
          status?: string
          updated_at?: string
        }
        Update: {
          action_taken?: string | null
          assigned_to?: string | null
          category?: string
          created_at?: string
          description?: string
          evidence_urls?: string[]
          id?: string
          priority?: string
          report_type?: string
          reported_message_id?: string | null
          reported_profile_id?: string | null
          reported_review_id?: string | null
          reported_vehicle_id?: string | null
          reporter_id?: string
          resolution_notes?: string | null
          resolved_at?: string | null
          reviewed_at?: string | null
          status?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'reports_assigned_to_fkey'
            columns: ['assigned_to']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'reports_reported_message_id_fkey'
            columns: ['reported_message_id']
            isOneToOne: false
            referencedRelation: 'messages'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'reports_reported_profile_id_fkey'
            columns: ['reported_profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'reports_reported_review_id_fkey'
            columns: ['reported_review_id']
            isOneToOne: false
            referencedRelation: 'reviews'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'reports_reported_vehicle_id_fkey'
            columns: ['reported_vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'reports_reporter_id_fkey'
            columns: ['reporter_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      reviews: {
        Row: {
          comment: string | null
          created_at: string
          hidden_reason: string | null
          id: string
          is_visible: boolean
          rating: number
          rental_id: string
          response: string | null
          response_at: string | null
          reviewer_id: string
          updated_at: string
        }
        Insert: {
          comment?: string | null
          created_at?: string
          hidden_reason?: string | null
          id?: string
          is_visible?: boolean
          rating: number
          rental_id: string
          response?: string | null
          response_at?: string | null
          reviewer_id: string
          updated_at?: string
        }
        Update: {
          comment?: string | null
          created_at?: string
          hidden_reason?: string | null
          id?: string
          is_visible?: boolean
          rating?: number
          rental_id?: string
          response?: string | null
          response_at?: string | null
          reviewer_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'reviews_rental_id_fkey'
            columns: ['rental_id']
            isOneToOne: false
            referencedRelation: 'rentals'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'reviews_reviewer_id_fkey'
            columns: ['reviewer_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      saved_searches: {
        Row: {
          categories: Database['public']['Enums']['vehicle_category'][] | null
          created_at: string
          end_date: string | null
          features: string[] | null
          fuel_type: Database['public']['Enums']['fuel_type'] | null
          id: string
          last_notified_at: string | null
          location: unknown
          location_name: string | null
          max_price_cents: number | null
          min_price_cents: number | null
          min_seats: number | null
          name: string
          notify_new_matches: boolean
          profile_id: string
          radius_km: number | null
          start_date: string | null
          transmission: Database['public']['Enums']['transmission_type'] | null
          updated_at: string
        }
        Insert: {
          categories?: Database['public']['Enums']['vehicle_category'][] | null
          created_at?: string
          end_date?: string | null
          features?: string[] | null
          fuel_type?: Database['public']['Enums']['fuel_type'] | null
          id?: string
          last_notified_at?: string | null
          location?: unknown
          location_name?: string | null
          max_price_cents?: number | null
          min_price_cents?: number | null
          min_seats?: number | null
          name: string
          notify_new_matches?: boolean
          profile_id: string
          radius_km?: number | null
          start_date?: string | null
          transmission?: Database['public']['Enums']['transmission_type'] | null
          updated_at?: string
        }
        Update: {
          categories?: Database['public']['Enums']['vehicle_category'][] | null
          created_at?: string
          end_date?: string | null
          features?: string[] | null
          fuel_type?: Database['public']['Enums']['fuel_type'] | null
          id?: string
          last_notified_at?: string | null
          location?: unknown
          location_name?: string | null
          max_price_cents?: number | null
          min_price_cents?: number | null
          min_seats?: number | null
          name?: string
          notify_new_matches?: boolean
          profile_id?: string
          radius_km?: number | null
          start_date?: string | null
          transmission?: Database['public']['Enums']['transmission_type'] | null
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'saved_searches_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      selfie_verifications: {
        Row: {
          callback_url: string | null
          created_at: string
          expires_at: string | null
          face_match_score: number | null
          id: string
          identity_document_id: string | null
          liveness_score: number | null
          processed_webhook_event_id: string | null
          profile_id: string
          provider: string
          provider_response: Json | null
          provider_session_id: string
          provider_status: string | null
          rejection_reason: string | null
          session_url: string | null
          started_at: string | null
          status: string
          updated_at: string
          verified_at: string | null
        }
        Insert: {
          callback_url?: string | null
          created_at?: string
          expires_at?: string | null
          face_match_score?: number | null
          id?: string
          identity_document_id?: string | null
          liveness_score?: number | null
          processed_webhook_event_id?: string | null
          profile_id: string
          provider?: string
          provider_response?: Json | null
          provider_session_id: string
          provider_status?: string | null
          rejection_reason?: string | null
          session_url?: string | null
          started_at?: string | null
          status?: string
          updated_at?: string
          verified_at?: string | null
        }
        Update: {
          callback_url?: string | null
          created_at?: string
          expires_at?: string | null
          face_match_score?: number | null
          id?: string
          identity_document_id?: string | null
          liveness_score?: number | null
          processed_webhook_event_id?: string | null
          profile_id?: string
          provider?: string
          provider_response?: Json | null
          provider_session_id?: string
          provider_status?: string | null
          rejection_reason?: string | null
          session_url?: string | null
          started_at?: string | null
          status?: string
          updated_at?: string
          verified_at?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'selfie_verifications_identity_document_id_fkey'
            columns: ['identity_document_id']
            isOneToOne: false
            referencedRelation: 'identity_documents'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'selfie_verifications_processed_webhook_event_id_fkey'
            columns: ['processed_webhook_event_id']
            isOneToOne: false
            referencedRelation: 'verification_webhook_events'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'selfie_verifications_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      spatial_ref_sys: {
        Row: {
          auth_name: string | null
          auth_srid: number | null
          proj4text: string | null
          srid: number
          srtext: string | null
        }
        Insert: {
          auth_name?: string | null
          auth_srid?: number | null
          proj4text?: string | null
          srid: number
          srtext?: string | null
        }
        Update: {
          auth_name?: string | null
          auth_srid?: number | null
          proj4text?: string | null
          srid?: number
          srtext?: string | null
        }
        Relationships: []
      }
      storage_purge_queue: {
        Row: {
          attempts: number
          bucket_id: string
          enqueued_at: string
          id: string
          last_error: string | null
          object_path: string
          profile_id: string | null
          purged_at: string | null
          reason: string
        }
        Insert: {
          attempts?: number
          bucket_id: string
          enqueued_at?: string
          id?: string
          last_error?: string | null
          object_path: string
          profile_id?: string | null
          purged_at?: string | null
          reason: string
        }
        Update: {
          attempts?: number
          bucket_id?: string
          enqueued_at?: string
          id?: string
          last_error?: string | null
          object_path?: string
          profile_id?: string | null
          purged_at?: string | null
          reason?: string
        }
        Relationships: [
          {
            foreignKeyName: 'storage_purge_queue_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      system_events: {
        Row: {
          created_at: string
          error: string | null
          event_type: string
          id: string
          payload: Json
          processed: boolean
          processed_at: string | null
          source: string
        }
        Insert: {
          created_at?: string
          error?: string | null
          event_type: string
          id?: string
          payload?: Json
          processed?: boolean
          processed_at?: string | null
          source: string
        }
        Update: {
          created_at?: string
          error?: string | null
          event_type?: string
          id?: string
          payload?: Json
          processed?: boolean
          processed_at?: string | null
          source?: string
        }
        Relationships: []
      }
      transactional_email_dedupe: {
        Row: {
          dedupe_key: string
          first_sent_at: string
          last_attempted_at: string
          recipient: string
          send_count: number
          template: string
        }
        Insert: {
          dedupe_key: string
          first_sent_at?: string
          last_attempted_at?: string
          recipient: string
          send_count?: number
          template: string
        }
        Update: {
          dedupe_key?: string
          first_sent_at?: string
          last_attempted_at?: string
          recipient?: string
          send_count?: number
          template?: string
        }
        Relationships: []
      }
      user_blocks: {
        Row: {
          blocked_id: string
          blocker_id: string
          created_at: string
          id: string
          reason: string | null
        }
        Insert: {
          blocked_id: string
          blocker_id: string
          created_at?: string
          id?: string
          reason?: string | null
        }
        Update: {
          blocked_id?: string
          blocker_id?: string
          created_at?: string
          id?: string
          reason?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'user_blocks_blocked_id_fkey'
            columns: ['blocked_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'user_blocks_blocker_id_fkey'
            columns: ['blocker_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      user_sessions: {
        Row: {
          app_build_version: string | null
          app_version: string | null
          device_id: string | null
          device_name: string | null
          device_type: string | null
          ended_at: string | null
          id: string
          ip_address: unknown
          is_current: boolean
          last_active_at: string
          location_city: string | null
          location_country: string | null
          location_region: string | null
          os_name: string | null
          os_version: string | null
          profile_id: string
          push_provider: string | null
          push_token: string | null
          started_at: string
        }
        Insert: {
          app_build_version?: string | null
          app_version?: string | null
          device_id?: string | null
          device_name?: string | null
          device_type?: string | null
          ended_at?: string | null
          id?: string
          ip_address?: unknown
          is_current?: boolean
          last_active_at?: string
          location_city?: string | null
          location_country?: string | null
          location_region?: string | null
          os_name?: string | null
          os_version?: string | null
          profile_id: string
          push_provider?: string | null
          push_token?: string | null
          started_at?: string
        }
        Update: {
          app_build_version?: string | null
          app_version?: string | null
          device_id?: string | null
          device_name?: string | null
          device_type?: string | null
          ended_at?: string | null
          id?: string
          ip_address?: unknown
          is_current?: boolean
          last_active_at?: string
          location_city?: string | null
          location_country?: string | null
          location_region?: string | null
          os_name?: string | null
          os_version?: string | null
          profile_id?: string
          push_provider?: string | null
          push_token?: string | null
          started_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'user_sessions_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      vehicle_availability: {
        Row: {
          created_at: string
          end_date: string
          id: string
          is_available: boolean
          reason: string | null
          start_date: string
          updated_at: string
          vehicle_id: string
        }
        Insert: {
          created_at?: string
          end_date: string
          id?: string
          is_available?: boolean
          reason?: string | null
          start_date: string
          updated_at?: string
          vehicle_id: string
        }
        Update: {
          created_at?: string
          end_date?: string
          id?: string
          is_available?: boolean
          reason?: string | null
          start_date?: string
          updated_at?: string
          vehicle_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'vehicle_availability_vehicle_id_fkey'
            columns: ['vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
        ]
      }
      vehicle_documents: {
        Row: {
          capture_source: string
          captured_at: string | null
          created_at: string
          document_number: string | null
          document_sha256: string | null
          document_type: string
          expiry_date: string | null
          id: string
          issue_date: string | null
          rejection_reason: string | null
          status: Database['public']['Enums']['verification_status']
          storage_path: string | null
          updated_at: string
          vehicle_id: string
          verification_payload: Json
          verified_at: string | null
          verified_by: string | null
        }
        Insert: {
          capture_source?: string
          captured_at?: string | null
          created_at?: string
          document_number?: string | null
          document_sha256?: string | null
          document_type: string
          expiry_date?: string | null
          id?: string
          issue_date?: string | null
          rejection_reason?: string | null
          status?: Database['public']['Enums']['verification_status']
          storage_path?: string | null
          updated_at?: string
          vehicle_id: string
          verification_payload?: Json
          verified_at?: string | null
          verified_by?: string | null
        }
        Update: {
          capture_source?: string
          captured_at?: string | null
          created_at?: string
          document_number?: string | null
          document_sha256?: string | null
          document_type?: string
          expiry_date?: string | null
          id?: string
          issue_date?: string | null
          rejection_reason?: string | null
          status?: Database['public']['Enums']['verification_status']
          storage_path?: string | null
          updated_at?: string
          vehicle_id?: string
          verification_payload?: Json
          verified_at?: string | null
          verified_by?: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'vehicle_documents_vehicle_id_fkey'
            columns: ['vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'vehicle_documents_verified_by_fkey'
            columns: ['verified_by']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      vehicle_images: {
        Row: {
          blurhash: string | null
          capture_source: string
          captured_at: string | null
          created_at: string
          height: number | null
          id: string
          is_primary: boolean
          mime_type: string | null
          photo_sha256: string | null
          position: number
          requirement_id: string
          size_bytes: number | null
          slot_index: number
          storage_path: string
          url: string
          vehicle_id: string
          verification_payload: Json
          width: number | null
        }
        Insert: {
          blurhash?: string | null
          capture_source?: string
          captured_at?: string | null
          created_at?: string
          height?: number | null
          id?: string
          is_primary?: boolean
          mime_type?: string | null
          photo_sha256?: string | null
          position?: number
          requirement_id: string
          size_bytes?: number | null
          slot_index?: number
          storage_path: string
          url: string
          vehicle_id: string
          verification_payload?: Json
          width?: number | null
        }
        Update: {
          blurhash?: string | null
          capture_source?: string
          captured_at?: string | null
          created_at?: string
          height?: number | null
          id?: string
          is_primary?: boolean
          mime_type?: string | null
          photo_sha256?: string | null
          position?: number
          requirement_id?: string
          size_bytes?: number | null
          slot_index?: number
          storage_path?: string
          url?: string
          vehicle_id?: string
          verification_payload?: Json
          width?: number | null
        }
        Relationships: [
          {
            foreignKeyName: 'vehicle_images_vehicle_id_fkey'
            columns: ['vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
        ]
      }
      vehicle_insurance_review_evidence_decisions: {
        Row: {
          created_at: string
          decided_at: string
          decided_by: string | null
          decision: string
          detail: string | null
          evidence_id: string
          evidence_type: string
          label: string
          requirement_key: string
          review_id: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          decided_at?: string
          decided_by?: string | null
          decision: string
          detail?: string | null
          evidence_id: string
          evidence_type: string
          label: string
          requirement_key: string
          review_id: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          decided_at?: string
          decided_by?: string | null
          decision?: string
          detail?: string | null
          evidence_id?: string
          evidence_type?: string
          label?: string
          requirement_key?: string
          review_id?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'vehicle_insurance_review_evidence_decisions_review_id_fkey'
            columns: ['review_id']
            isOneToOne: false
            referencedRelation: 'vehicle_insurance_reviews'
            referencedColumns: ['id']
          },
        ]
      }
      vehicle_insurance_review_links: {
        Row: {
          created_at: string
          decision: string | null
          decision_at: string | null
          decision_reason: string | null
          expires_at: string
          id: string
          last_opened_at: string | null
          opened_count: number
          purpose: string
          review_id: string
          revoked_at: string | null
          token_hash: string
          updated_at: string
        }
        Insert: {
          created_at?: string
          decision?: string | null
          decision_at?: string | null
          decision_reason?: string | null
          expires_at: string
          id?: string
          last_opened_at?: string | null
          opened_count?: number
          purpose?: string
          review_id: string
          revoked_at?: string | null
          token_hash: string
          updated_at?: string
        }
        Update: {
          created_at?: string
          decision?: string | null
          decision_at?: string | null
          decision_reason?: string | null
          expires_at?: string
          id?: string
          last_opened_at?: string | null
          opened_count?: number
          purpose?: string
          review_id?: string
          revoked_at?: string | null
          token_hash?: string
          updated_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'vehicle_insurance_review_links_review_id_fkey'
            columns: ['review_id']
            isOneToOne: false
            referencedRelation: 'vehicle_insurance_reviews'
            referencedColumns: ['id']
          },
        ]
      }
      vehicle_insurance_reviews: {
        Row: {
          completed_at: string | null
          created_at: string
          due_at: string
          evidence_hash: string | null
          id: string
          last_error: string | null
          last_synced_at: string | null
          partner: string
          partner_request: Json
          partner_response: Json
          partner_submission_id: string | null
          rejection_reasons: string[]
          result_summary: string | null
          status: string
          submitted_at: string | null
          sum_insured_cents: number | null
          updated_at: string
          vehicle_id: string
        }
        Insert: {
          completed_at?: string | null
          created_at?: string
          due_at?: string
          evidence_hash?: string | null
          id?: string
          last_error?: string | null
          last_synced_at?: string | null
          partner?: string
          partner_request?: Json
          partner_response?: Json
          partner_submission_id?: string | null
          rejection_reasons?: string[]
          result_summary?: string | null
          status?: string
          submitted_at?: string | null
          sum_insured_cents?: number | null
          updated_at?: string
          vehicle_id: string
        }
        Update: {
          completed_at?: string | null
          created_at?: string
          due_at?: string
          evidence_hash?: string | null
          id?: string
          last_error?: string | null
          last_synced_at?: string | null
          partner?: string
          partner_request?: Json
          partner_response?: Json
          partner_submission_id?: string | null
          rejection_reasons?: string[]
          result_summary?: string | null
          status?: string
          submitted_at?: string | null
          sum_insured_cents?: number | null
          updated_at?: string
          vehicle_id?: string
        }
        Relationships: [
          {
            foreignKeyName: 'vehicle_insurance_reviews_vehicle_id_fkey'
            columns: ['vehicle_id']
            isOneToOne: false
            referencedRelation: 'vehicles'
            referencedColumns: ['id']
          },
        ]
      }
      vehicle_makes: {
        Row: {
          created_at: string
          id: string
          is_active: boolean
          logo_url: string | null
          name: string
        }
        Insert: {
          created_at?: string
          id?: string
          is_active?: boolean
          logo_url?: string | null
          name: string
        }
        Update: {
          created_at?: string
          id?: string
          is_active?: boolean
          logo_url?: string | null
          name?: string
        }
        Relationships: []
      }
      vehicle_models: {
        Row: {
          category: Database['public']['Enums']['vehicle_category']
          created_at: string
          default_doors: number
          default_features: string[]
          default_fuel_type: Database['public']['Enums']['fuel_type']
          default_seats: number
          default_transmission: Database['public']['Enums']['transmission_type']
          id: string
          is_active: boolean
          make_id: string
          name: string
          year_end: number | null
          year_start: number | null
        }
        Insert: {
          category: Database['public']['Enums']['vehicle_category']
          created_at?: string
          default_doors?: number
          default_features?: string[]
          default_fuel_type?: Database['public']['Enums']['fuel_type']
          default_seats?: number
          default_transmission?: Database['public']['Enums']['transmission_type']
          id?: string
          is_active?: boolean
          make_id: string
          name: string
          year_end?: number | null
          year_start?: number | null
        }
        Update: {
          category?: Database['public']['Enums']['vehicle_category']
          created_at?: string
          default_doors?: number
          default_features?: string[]
          default_fuel_type?: Database['public']['Enums']['fuel_type']
          default_seats?: number
          default_transmission?: Database['public']['Enums']['transmission_type']
          id?: string
          is_active?: boolean
          make_id?: string
          name?: string
          year_end?: number | null
          year_start?: number | null
        }
        Relationships: [
          {
            foreignKeyName: 'vehicle_models_make_id_fkey'
            columns: ['make_id']
            isOneToOne: false
            referencedRelation: 'vehicle_makes'
            referencedColumns: ['id']
          },
        ]
      }
      vehicles: {
        Row: {
          category: Database['public']['Enums']['vehicle_category']
          city_id: string | null
          cleaning_fee_cents: number | null
          color: string
          company_owner_detected: boolean
          country_id: string
          coverage_daily_rate_cents: number | null
          coverage_rate_quoted_at: string | null
          coverage_path: string
          coverage_provider: string | null
          coverage_review_due_at: string | null
          coverage_review_status: string
          coverage_sum_insured_cents: number | null
          created_at: string
          daily_km_limit: number | null
          daily_rate_cents: number
          deleted_at: string | null
          delivery_radius_km: number | null
          description: string | null
          detected_legal_name: string | null
          doors: number
          excess_km_rate_cents: number | null
          features: string[]
          fuel_type: Database['public']['Enums']['fuel_type']
          home_address: string | null
          home_location: unknown
          host_id: string
          hourly_rate_cents: number | null
          id: string
          insurance_expiry: string | null
          insurance_model: Database['public']['Enums']['insurance_model']
          insurance_policy_number: string | null
          is_instant_book: boolean
          make_id: string
          min_rental_hours: number | null
          model_id: string
          monthly_discount_percent: number | null
          odometer_km: number
          organization_id: string | null
          ownership_authorized_by_name: string | null
          ownership_declared_at: string | null
          ownership_relationship: string | null
          ownership_type: string | null
          plate_country: string
          plate_number: string
          published_at: string | null
          qualitas_candidates: Json | null
          qualitas_catalog_version: string | null
          qualitas_clave_amis: string | null
          qualitas_resolved_at: string | null
          rating_avg: number | null
          rating_count: number
          rules: string | null
          seats: number
          security_deposit_cents: number
          status: Database['public']['Enums']['vehicle_status']
          status_reason: string | null
          title: string
          transmission: Database['public']['Enums']['transmission_type']
          rented_days: number
          trip_count: number
          updated_at: string
          vin: string | null
          weekly_discount_percent: number | null
          year: number
        }
        Insert: {
          category: Database['public']['Enums']['vehicle_category']
          city_id?: string | null
          cleaning_fee_cents?: number | null
          color: string
          company_owner_detected?: boolean
          country_id: string
          coverage_daily_rate_cents?: number | null
          coverage_rate_quoted_at?: string | null
          coverage_path?: string
          coverage_provider?: string | null
          coverage_review_due_at?: string | null
          coverage_review_status?: string
          coverage_sum_insured_cents?: number | null
          created_at?: string
          daily_km_limit?: number | null
          daily_rate_cents: number
          deleted_at?: string | null
          delivery_radius_km?: number | null
          description?: string | null
          detected_legal_name?: string | null
          doors: number
          excess_km_rate_cents?: number | null
          features?: string[]
          fuel_type: Database['public']['Enums']['fuel_type']
          home_address?: string | null
          home_location?: unknown
          host_id: string
          hourly_rate_cents?: number | null
          id?: string
          insurance_expiry?: string | null
          insurance_model?: Database['public']['Enums']['insurance_model']
          insurance_policy_number?: string | null
          is_instant_book?: boolean
          make_id: string
          min_rental_hours?: number | null
          model_id: string
          monthly_discount_percent?: number | null
          odometer_km?: number
          organization_id?: string | null
          ownership_authorized_by_name?: string | null
          ownership_declared_at?: string | null
          ownership_relationship?: string | null
          ownership_type?: string | null
          plate_country: string
          plate_number: string
          published_at?: string | null
          qualitas_candidates?: Json | null
          qualitas_catalog_version?: string | null
          qualitas_clave_amis?: string | null
          qualitas_resolved_at?: string | null
          rating_avg?: number | null
          rating_count?: number
          rules?: string | null
          seats: number
          security_deposit_cents: number
          status?: Database['public']['Enums']['vehicle_status']
          status_reason?: string | null
          title: string
          transmission: Database['public']['Enums']['transmission_type']
          trip_count?: number
          updated_at?: string
          vin?: string | null
          weekly_discount_percent?: number | null
          year: number
        }
        Update: {
          category?: Database['public']['Enums']['vehicle_category']
          city_id?: string | null
          cleaning_fee_cents?: number | null
          color?: string
          company_owner_detected?: boolean
          country_id?: string
          coverage_review_due_at?: string | null
          coverage_path?: string
          coverage_provider?: string | null
          coverage_review_status?: string
          coverage_sum_insured_cents?: number | null
          created_at?: string
          daily_km_limit?: number | null
          daily_rate_cents?: number
          deleted_at?: string | null
          delivery_radius_km?: number | null
          description?: string | null
          detected_legal_name?: string | null
          doors?: number
          excess_km_rate_cents?: number | null
          features?: string[]
          fuel_type?: Database['public']['Enums']['fuel_type']
          home_address?: string | null
          home_location?: unknown
          host_id?: string
          hourly_rate_cents?: number | null
          id?: string
          insurance_expiry?: string | null
          insurance_model?: Database['public']['Enums']['insurance_model']
          insurance_policy_number?: string | null
          is_instant_book?: boolean
          make_id?: string
          min_rental_hours?: number | null
          model_id?: string
          monthly_discount_percent?: number | null
          odometer_km?: number
          organization_id?: string | null
          ownership_authorized_by_name?: string | null
          ownership_declared_at?: string | null
          ownership_relationship?: string | null
          ownership_type?: string | null
          plate_country?: string
          plate_number?: string
          published_at?: string | null
          qualitas_candidates?: Json | null
          qualitas_catalog_version?: string | null
          qualitas_clave_amis?: string | null
          qualitas_resolved_at?: string | null
          rating_avg?: number | null
          rating_count?: number
          rules?: string | null
          seats?: number
          security_deposit_cents?: number
          status?: Database['public']['Enums']['vehicle_status']
          status_reason?: string | null
          title?: string
          transmission?: Database['public']['Enums']['transmission_type']
          rented_days?: number
          trip_count?: number
          updated_at?: string
          vin?: string | null
          weekly_discount_percent?: number | null
          year?: number
        }
        Relationships: [
          {
            foreignKeyName: 'vehicles_city_id_fkey'
            columns: ['city_id']
            isOneToOne: false
            referencedRelation: 'cities'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'vehicles_country_id_fkey'
            columns: ['country_id']
            isOneToOne: false
            referencedRelation: 'countries'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'vehicles_host_id_fkey'
            columns: ['host_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'vehicles_make_id_fkey'
            columns: ['make_id']
            isOneToOne: false
            referencedRelation: 'vehicle_makes'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'vehicles_model_id_fkey'
            columns: ['model_id']
            isOneToOne: false
            referencedRelation: 'vehicle_models'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'vehicles_organization_id_fkey'
            columns: ['organization_id']
            isOneToOne: false
            referencedRelation: 'organizations'
            referencedColumns: ['id']
          },
        ]
      }
      verification_webhook_events: {
        Row: {
          event_type: string | null
          id: string
          processed: boolean | null
          processing_error: string | null
          provider: string
          raw_body: Json | null
          raw_headers: Json | null
          received_at: string | null
          session_id: string | null
          signature_valid: boolean | null
        }
        Insert: {
          event_type?: string | null
          id?: string
          processed?: boolean | null
          processing_error?: string | null
          provider: string
          raw_body?: Json | null
          raw_headers?: Json | null
          received_at?: string | null
          session_id?: string | null
          signature_valid?: boolean | null
        }
        Update: {
          event_type?: string | null
          id?: string
          processed?: boolean | null
          processing_error?: string | null
          provider?: string
          raw_body?: Json | null
          raw_headers?: Json | null
          received_at?: string | null
          session_id?: string | null
          signature_valid?: boolean | null
        }
        Relationships: []
      }
      waitlist_audit: {
        Row: {
          created_at: string
          email: string | null
          event: string
          id: string
          ip_address: string | null
          metadata: Json | null
          user_agent: string | null
        }
        Insert: {
          created_at?: string
          email?: string | null
          event: string
          id?: string
          ip_address?: string | null
          metadata?: Json | null
          user_agent?: string | null
        }
        Update: {
          created_at?: string
          email?: string | null
          event?: string
          id?: string
          ip_address?: string | null
          metadata?: Json | null
          user_agent?: string | null
        }
        Relationships: []
      }
      waitlist_signups: {
        Row: {
          consent_timestamp: string | null
          consent_version: string | null
          created_at: string
          email: string
          id: string
          ip_address: string | null
          source: string
          user_agent: string | null
        }
        Insert: {
          consent_timestamp?: string | null
          consent_version?: string | null
          created_at?: string
          email: string
          id?: string
          ip_address?: string | null
          source?: string
          user_agent?: string | null
        }
        Update: {
          consent_timestamp?: string | null
          consent_version?: string | null
          created_at?: string
          email?: string
          id?: string
          ip_address?: string | null
          source?: string
          user_agent?: string | null
        }
        Relationships: []
      }
      wompi_webhook_events: {
        Row: {
          amount_cents: number | null
          approved: boolean | null
          id: string
          outcome: string
          outcome_detail: string | null
          payment_id: string | null
          received_at: string
          rental_id: string | null
          result: string | null
          signature_hash_prefix: string | null
          transaction_id: string
        }
        Insert: {
          amount_cents?: number | null
          approved?: boolean | null
          id?: string
          outcome: string
          outcome_detail?: string | null
          payment_id?: string | null
          received_at?: string
          rental_id?: string | null
          result?: string | null
          signature_hash_prefix?: string | null
          transaction_id: string
        }
        Update: {
          amount_cents?: number | null
          approved?: boolean | null
          id?: string
          outcome?: string
          outcome_detail?: string | null
          payment_id?: string | null
          received_at?: string
          rental_id?: string | null
          result?: string | null
          signature_hash_prefix?: string | null
          transaction_id?: string
        }
        Relationships: []
      }
    }
    Views: {
      geography_columns: {
        Row: {
          coord_dimension: number | null
          f_geography_column: unknown
          f_table_catalog: unknown
          f_table_name: unknown
          f_table_schema: unknown
          srid: number | null
          type: string | null
        }
        Relationships: []
      }
      geometry_columns: {
        Row: {
          coord_dimension: number | null
          f_geometry_column: unknown
          f_table_catalog: string | null
          f_table_name: unknown
          f_table_schema: unknown
          srid: number | null
          type: string | null
        }
        Insert: {
          coord_dimension?: number | null
          f_geometry_column?: unknown
          f_table_catalog?: string | null
          f_table_name?: unknown
          f_table_schema?: unknown
          srid?: number | null
          type?: string | null
        }
        Update: {
          coord_dimension?: number | null
          f_geometry_column?: unknown
          f_table_catalog?: string | null
          f_table_name?: unknown
          f_table_schema?: unknown
          srid?: number | null
          type?: string | null
        }
        Relationships: []
      }
      product_analytics_events_visible: {
        Row: {
          app_version: string | null
          attributed_at: string | null
          attribution_source: string | null
          client_event_id: string | null
          country_code: string | null
          created_at: string | null
          device_type: string | null
          duration_ms: number | null
          element_id: string | null
          element_type: string | null
          event_name: string | null
          event_type: string | null
          id: string | null
          installation_fingerprint: string | null
          interaction_id_quality: string | null
          interaction_label: string | null
          ip_country: string | null
          ip_country_source: string | null
          ip_fingerprint: string | null
          is_failure: boolean | null
          is_success: boolean | null
          locale: string | null
          metadata: Json | null
          occurred_at: string | null
          platform: string | null
          product_flow: string | null
          profile_display_name: string | null
          profile_email: string | null
          profile_first_name: string | null
          profile_id: string | null
          profile_last_name: string | null
          received_at: string | null
          route_path: string | null
          screen_name: string | null
          scroll_depth_pct: number | null
          session_id: string | null
          timezone: string | null
          user_agent_fingerprint: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'product_analytics_events_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      security_session_events_visible: {
        Row: {
          action: string | null
          actor_id: string | null
          created_at: string | null
          event_name: string | null
          id: string | null
          ip_address: unknown
          ip_country: string | null
          ip_country_source: string | null
          new_values: Json | null
          platform: string | null
          profile_display_name: string | null
          profile_email: string | null
          profile_first_name: string | null
          profile_last_name: string | null
          provider: string | null
          resource_id: string | null
          resource_type: string | null
          user_agent: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'audit_logs_actor_id_fkey'
            columns: ['actor_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
      security_user_sessions_visible: {
        Row: {
          app_version: string | null
          device_id: string | null
          device_name: string | null
          device_type: string | null
          id: string | null
          ip_address: unknown
          ip_country: string | null
          ip_country_source: string | null
          is_current: boolean | null
          last_active_at: string | null
          location_city: string | null
          location_country: string | null
          location_region: string | null
          os_name: string | null
          os_version: string | null
          profile_display_name: string | null
          profile_email: string | null
          profile_first_name: string | null
          profile_id: string | null
          profile_last_name: string | null
          started_at: string | null
        }
        Relationships: [
          {
            foreignKeyName: 'user_sessions_profile_id_fkey'
            columns: ['profile_id']
            isOneToOne: false
            referencedRelation: 'profiles'
            referencedColumns: ['id']
          },
        ]
      }
    }
    Functions: {
      coverage_eligibility_for_vehicle: {
        Args: { p_category: string; p_year: number | null }
        Returns: Json
      }
      _array_distinct_count: { Args: { arr: string[] }; Returns: number }
      _postgis_deprecate: {
        Args: { newname: string; oldname: string; version: string }
        Returns: undefined
      }
      _postgis_index_extent: {
        Args: { col: string; tbl: unknown }
        Returns: unknown
      }
      _postgis_pgsql_version: { Args: never; Returns: string }
      _postgis_scripts_pgsql_version: { Args: never; Returns: string }
      _postgis_selectivity: {
        Args: { att_name: string; geom: unknown; mode?: string; tbl: unknown }
        Returns: number
      }
      _postgis_stats: {
        Args: { ''?: string; att_name: string; tbl: unknown }
        Returns: string
      }
      _st_3dintersects: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      _st_contains: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      _st_containsproperly: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      _st_coveredby:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      _st_covers:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      _st_crosses: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      _st_dwithin: {
        Args: {
          geog1: unknown
          geog2: unknown
          tolerance: number
          use_spheroid?: boolean
        }
        Returns: boolean
      }
      _st_equals: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      _st_intersects: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      _st_linecrossingdirection: {
        Args: { line1: unknown; line2: unknown }
        Returns: number
      }
      _st_longestline: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      _st_maxdistance: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      _st_orderingequals: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      _st_overlaps: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      _st_sortablehash: { Args: { geom: unknown }; Returns: number }
      _st_touches: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      _st_voronoi: {
        Args: {
          clip?: unknown
          g1: unknown
          return_polygons?: boolean
          tolerance?: number
        }
        Returns: unknown
      }
      _st_within: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      accept_pending_organization_invites_for_profile: {
        Args: never
        Returns: number
      }
      activate_phone_manually: {
        Args: { p_phone: string; p_reason: string }
        Returns: {
          activation_id: string
          user_id: string
        }[]
      }
      addauth: { Args: { '': string }; Returns: boolean }
      addgeometrycolumn:
        | {
            Args: {
              catalog_name: string
              column_name: string
              new_dim: number
              new_srid_in: number
              new_type: string
              schema_name: string
              table_name: string
              use_typmod?: boolean
            }
            Returns: string
          }
        | {
            Args: {
              column_name: string
              new_dim: number
              new_srid: number
              new_type: string
              schema_name: string
              table_name: string
              use_typmod?: boolean
            }
            Returns: string
          }
        | {
            Args: {
              column_name: string
              new_dim: number
              new_srid: number
              new_type: string
              table_name: string
              use_typmod?: boolean
            }
            Returns: string
          }
      admin_city_counts: {
        Args: never
        Returns: Json
      }
      admin_decide_driver_license: {
        Args: {
          p_admin_email?: string
          p_decision: string
          p_license_id: string
          p_reason?: string
        }
        Returns: undefined
      }
      admin_decide_identity_document: {
        Args: {
          p_admin_email?: string
          p_decision: string
          p_document_id: string
          p_reason?: string
        }
        Returns: undefined
      }
      admin_decide_vehicle_document: {
        Args: {
          p_admin_email?: string
          p_decision: string
          p_document_id: string
          p_reason?: string
          p_vehicle_id: string
        }
        Returns: undefined
      }
      admin_decide_vehicle_insurance_review: {
        Args: {
          p_admin_email?: string
          p_decision: string
          p_evidence_decisions?: Json
          p_reason?: string
          p_review_id: string
          p_sum_insured_cents?: number
        }
        Returns: {
          completed_at: string | null
          created_at: string
          due_at: string
          evidence_hash: string | null
          id: string
          last_error: string | null
          last_synced_at: string | null
          partner: string
          partner_request: Json
          partner_response: Json
          partner_submission_id: string | null
          rejection_reasons: string[]
          result_summary: string | null
          status: string
          submitted_at: string | null
          sum_insured_cents: number | null
          updated_at: string
          vehicle_id: string
        }
        SetofOptions: {
          from: '*'
          to: 'vehicle_insurance_reviews'
          isOneToOne: true
          isSetofReturn: false
        }
      }
      admin_decide_vehicle_insurance_review_impl: {
        Args: {
          p_admin_email?: string
          p_decision: string
          p_evidence_decisions?: Json
          p_reason?: string
          p_review_id: string
          p_sum_insured_cents?: number
        }
        Returns: {
          completed_at: string | null
          created_at: string
          due_at: string
          evidence_hash: string | null
          id: string
          last_error: string | null
          last_synced_at: string | null
          partner: string
          partner_request: Json
          partner_response: Json
          partner_submission_id: string | null
          rejection_reasons: string[]
          result_summary: string | null
          status: string
          submitted_at: string | null
          sum_insured_cents: number | null
          updated_at: string
          vehicle_id: string
        }
        SetofOptions: {
          from: '*'
          to: 'vehicle_insurance_reviews'
          isOneToOne: true
          isSetofReturn: false
        }
      }
      admin_decide_vehicle_status: {
        Args: {
          p_admin_email?: string
          p_reason?: string
          p_status: string
          p_vehicle_id: string
        }
        Returns: undefined
      }
      admin_release_deposit: {
        Args: { p_operator_email?: string; p_rental_id: string }
        Returns: Json
      }
      admin_signup_daily_series: {
        Args: { p_days?: number }
        Returns: {
          bucket_day: string
          fixture_count: number
          fixture_pattern_count: number
          neighbour_real_median: number
          neighbour_total_median: number
          real_activated: number
          real_count: number
          real_is_outlier: boolean
          real_phone_confirmed: number
          real_with_rental: number
          real_with_vehicle: number
          total_count: number
          total_is_outlier: boolean
        }[]
      }
      admin_signup_fixture_origins: {
        Args: { p_days?: number }
        Returns: {
          account_class: string
          fixture_reason: string
          total_count: number
        }[]
      }
      admin_signup_hourly_peaks: {
        Args: { p_days?: number; p_limit?: number }
        Returns: {
          bucket_hour: string
          fixture_count: number
          fixture_pattern_count: number
          real_count: number
          real_rank: number
          total_count: number
          total_rank: number
        }[]
      }
      admin_signup_provider_split: {
        Args: { p_days?: number }
        Returns: {
          fixture_count: number
          fixture_pattern_count: number
          real_activated: number
          real_count: number
          real_phone_confirmed: number
          signup_provider: string
          total_count: number
        }[]
      }
      assert_phone_link_available: {
        Args: { p_phone: string; p_user_id: string }
        Returns: undefined
      }
      booking_eligibility: {
        Args: { p_requires_aml?: boolean }
        Returns: {
          allowed: boolean
          missing: string[]
        }[]
      }
      booking_eligibility_for: {
        Args: { p_profile_id: string; p_requires_aml?: boolean }
        Returns: {
          allowed: boolean
          missing: string[]
        }[]
      }
      calc_sv_nit_check_digit: { Args: { p_value: string }; Returns: string }
      can_message_host: { Args: { p_host_id: string }; Returns: boolean }
      conversation_call_phone: { Args: { p_conversation_id: string }; Returns: string }
      search_conversation_messages: {
        Args: { p_conversation_id: string; p_limit?: number; p_query: string }
        Returns: {
          content: string | null
          created_at: string
          id: string
          message_type: Database['public']['Enums']['message_type']
          rank: number
          sender_id: string
        }[]
      }
      cancel_account_deletion: { Args: never; Returns: undefined }
      capture_payment_and_confirm_rental: {
        Args: {
          p_amount_cents: number
          p_codigo_autorizacion: string
          p_es_productiva: boolean
          p_forma_pago: string
          p_is_approved: boolean
          p_payment_id: string
          p_provider_payment_id: string
          p_provider_response: Json
          p_rental_id: string
          p_status_reason: string
          p_wompi_transaction_id: string
        }
        Returns: Json
      }
      capture_payment_and_confirm_rental_provider: {
        Args: {
          p_amount_cents: number
          p_authorization_code: string
          p_is_approved: boolean
          p_is_productive: boolean
          p_payment_id: string
          p_payment_method: string
          p_provider: string
          p_provider_reference: string
          p_provider_response: Json
          p_rental_id: string
          p_status_reason: string
        }
        Returns: Json
      }
      check_rate_limit:
        | {
            Args: {
              p_action: string
              p_identifier: string
              p_identifier_type: string
              p_max_attempts?: number
              p_window_seconds?: number
            }
            Returns: {
              allowed: boolean
              remaining: number
              reset_at: string
            }[]
          }
        | {
            Args: {
              p_key: string
              p_max_attempts: number
              p_window_minutes: number
            }
            Returns: boolean
          }
      check_vehicle_booking_availability: {
        Args: {
          p_end_at: string
          p_ignore_rental_id?: string
          p_start_at: string
          p_timezone?: string
          p_vehicle_id: string
        }
        Returns: {
          blocked_availability_id: string
          conflicting_rental_id: string
          is_available: boolean
          reason: string
        }[]
      }
      completed_pair_rentals: {
        Args: { p_guest_id: string; p_host_id: string }
        Returns: number
      }
      claim_pending_dispute_settlements: {
        Args: { p_limit?: number }
        Returns: {
          action: Database['public']['Enums']['dispute_settlement_action']
          amount_cents: number
          attempt_count: number
          claimed_at: string | null
          completed_at: string | null
          created_at: string
          dispute_id: string
          id: string
          last_error: string | null
          provider_transaction_id: string | null
          rental_id: string
          status: Database['public']['Enums']['dispute_settlement_status']
          updated_at: string
        }[]
        SetofOptions: {
          from: '*'
          to: 'dispute_settlements'
          isOneToOne: false
          isSetofReturn: true
        }
      }
      claim_storage_purge_batch: {
        Args: { p_limit?: number; p_max_attempts?: number }
        Returns: {
          attempts: number
          bucket_id: string
          id: string
          object_path: string
        }[]
      }
      cleanup_expired_rate_limits: { Args: never; Returns: number }
      cleanup_prelaunch_operational_noise: { Args: never; Returns: Json }
      complete_signup: {
        Args: {
          p_accept_marketing?: boolean
          p_ip_address?: unknown
          p_profile_id: string
          p_user_agent?: string
        }
        Returns: undefined
      }
      compute_rental_late_fee: {
        Args: {
          p_actual_end_at: string
          p_daily_rate_cents: number
          p_end_at: string
        }
        Returns: number
      }
      coverage_decide_vehicle_insurance_review_with_link: {
        Args: {
          p_decision: string
          p_evidence_decisions?: Json
          p_reason?: string
          p_sum_insured_cents?: number
          p_token_hash: string
        }
        Returns: {
          completed_at: string | null
          created_at: string
          due_at: string
          evidence_hash: string | null
          id: string
          last_error: string | null
          last_synced_at: string | null
          partner: string
          partner_request: Json
          partner_response: Json
          partner_submission_id: string | null
          rejection_reasons: string[]
          result_summary: string | null
          status: string
          submitted_at: string | null
          sum_insured_cents: number | null
          updated_at: string
          vehicle_id: string
        }
        SetofOptions: {
          from: '*'
          to: 'vehicle_insurance_reviews'
          isOneToOne: true
          isSetofReturn: false
        }
      }
      coverage_next_business_day_deadline: {
        Args: { p_start?: string }
        Returns: string
      }
      coverage_pick_vehicle_qualitas_version: {
        Args: {
          p_actor_email?: string
          p_clave_amis: string
          p_token_hash: string
        }
        Returns: Json
      }
      coverage_save_vehicle_review_evidence_decision: {
        Args: {
          p_actor_email?: string
          p_decision: string
          p_detail?: string
          p_evidence_id: string
          p_evidence_type: string
          p_token_hash: string
        }
        Returns: Json
      }
      coverage_store_vehicle_review_evidence_decisions: {
        Args: {
          p_actor_email?: string
          p_evidence_decisions: Json
          p_review_id: string
        }
        Returns: Json
      }
      create_organization_member_invite: {
        Args: { p_email?: string; p_organization_id: string; p_phone?: string }
        Returns: Json
      }
      create_organization_with_owner: {
        Args: {
          p_city_id?: string
          p_country_id?: string
          p_email: string
          p_insurance_model?: string
          p_legal_name: string
          p_name: string
          p_phone?: string
          p_tax_id: string
        }
        Returns: {
          address_city: string | null
          address_line1: string | null
          address_line2: string | null
          address_postal_code: string | null
          address_region: string | null
          allows_individual_hosts: boolean
          city_id: string | null
          country_id: string
          created_at: string
          email: string
          id: string
          insurance_model: Database['public']['Enums']['insurance_model']
          is_active: boolean
          is_verified: boolean
          legal_name: string | null
          logo_blurhash: string | null
          logo_url: string | null
          marketplace_visibility_bypass: boolean
          name: string
          phone: string | null
          tax_id: string | null
          updated_at: string
          verified_at: string | null
          website: string | null
        }
        SetofOptions: {
          from: '*'
          to: 'organizations'
          isOneToOne: true
          isSetofReturn: false
        }
      }
      current_profile: {
        Args: never
        Returns: {
          avatar_blurhash: string | null
          avatar_url: string | null
          city_id: string | null
          country_id: string | null
          created_at: string
          data_blocked_at: string | null
          date_of_birth: string | null
          delete_requested_at: string | null
          delete_scheduled_at: string | null
          deleted_at: string | null
          deletion_reason: string | null
          display_name: string | null
          email: string
          first_name: string | null
          guest_rating_avg: number | null
          guest_rating_count: number
          host_rating_avg: number | null
          host_rating_count: number
          id: string
          is_host_enabled: boolean
          is_suspended: boolean
          is_verified: boolean
          last_active_at: string | null
          last_name: string | null
          marketplace_visibility_bypass: boolean
          phone: string | null
          preferred_currency: string | null
          preferred_locale: string | null
          roles: Database['public']['Enums']['user_role'][]
          suspended_reason: string | null
          suspended_until: string | null
          updated_at: string
        }
        SetofOptions: {
          from: '*'
          to: 'profiles'
          isOneToOne: true
          isSetofReturn: false
        }
      }
      deactivate_payment_method: {
        Args: { p_payment_method_id: string }
        Returns: {
          billing_country: string | null
          billing_name: string | null
          card_brand: string | null
          card_exp_month: number | null
          card_exp_year: number | null
          card_fingerprint: string | null
          card_last4: string | null
          created_at: string
          deactivated_at: string | null
          id: string
          is_active: boolean
          is_default: boolean
          label: string | null
          method_type: Database['public']['Enums']['payment_method_type']
          profile_id: string
          provider: string
          provider_token: string
          updated_at: string
        }
        SetofOptions: {
          from: '*'
          to: 'payment_methods'
          isOneToOne: true
          isSetofReturn: false
        }
      }
      disablelongtransactions: { Args: never; Returns: string }
      dropgeometrycolumn:
        | {
            Args: {
              catalog_name: string
              column_name: string
              schema_name: string
              table_name: string
            }
            Returns: string
          }
        | {
            Args: {
              column_name: string
              schema_name: string
              table_name: string
            }
            Returns: string
          }
        | { Args: { column_name: string; table_name: string }; Returns: string }
      dropgeometrytable:
        | {
            Args: {
              catalog_name: string
              schema_name: string
              table_name: string
            }
            Returns: string
          }
        | { Args: { schema_name: string; table_name: string }; Returns: string }
        | { Args: { table_name: string }; Returns: string }
      enablelongtransactions: { Args: never; Returns: string }
      equals: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      escalate_overdue_disputes: { Args: never; Returns: number }
      expire_abandoned_payment_holds: { Args: never; Returns: number }
      release_unpaid_rental_hold: { Args: { p_rental_id: string }; Returns: boolean }
      fire_pre_trip_reminders: { Args: never; Returns: number }
      fire_stale_build_notices: { Args: never; Returns: undefined }
      fire_trip_reminders: { Args: never; Returns: number }
      fn_category_allowed: {
        Args: { p_category: string; p_profile_id: string }
        Returns: boolean
      }
      fn_channel_allowed: {
        Args: {
          p_channel: Database['public']['Enums']['notification_channel']
          p_profile_id: string
        }
        Returns: boolean
      }
      fn_hard_delete_account: {
        Args: { p_profile_id: string }
        Returns: undefined
      }
      fn_notify:
        | {
            Args: {
              p_body: string
              p_channel?: Database['public']['Enums']['notification_channel']
              p_data?: Json
              p_profile_id: string
              p_title: string
            }
            Returns: string
          }
        | {
            Args: {
              p_body: string
              p_category: string
              p_channel: Database['public']['Enums']['notification_channel']
              p_data: Json
              p_profile_id: string
              p_title: string
            }
            Returns: string
          }
      fn_org_earnings_summary: {
        Args: { p_org_id: string }
        Returns: {
          captured_count: number
          captured_total_cents: number
          mtd_cents: number
          pending_cents: number
          ytd_cents: number
        }[]
      }
      fn_send_email: {
        Args: {
          p_data: Json
          p_subject: string
          p_template: string
          p_to: string
        }
        Returns: undefined
      }
      fn_should_notify: {
        Args: {
          p_category: string
          p_channel: Database['public']['Enums']['notification_channel']
          p_profile_id: string
        }
        Returns: boolean
      }
      format_sv_nit: { Args: { p_value: string }; Returns: string }
      generate_ulid: { Args: never; Returns: string }
      geometry: { Args: { '': string }; Returns: unknown }
      geometry_above: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_below: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_cmp: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      geometry_contained_3d: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_contains: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_contains_3d: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_distance_box: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      geometry_distance_centroid: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      geometry_eq: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_ge: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_gt: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_le: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_left: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_lt: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_overabove: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_overbelow: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_overlaps: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_overlaps_3d: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_overleft: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_overright: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_right: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_same: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_same_3d: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geometry_within: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      geomfromewkt: { Args: { '': string }; Returns: unknown }
      get_blocked_profiles_for_viewer: {
        Args: never
        Returns: {
          avatar_url: string
          block_id: string
          blocked_id: string
          created_at: string
          display_name: string
          reason: string
        }[]
      }
      get_org_conversations: {
        Args: { p_org_id: string }
        Returns: {
          created_at: string
          id: string
          is_archived: boolean
          last_message_at: string | null
          last_message_preview: string | null
          participant_ids: string[]
          rental_id: string | null
          updated_at: string
        }[]
        SetofOptions: {
          from: '*'
          to: 'conversations'
          isOneToOne: false
          isSetofReturn: true
        }
      }
      get_public_coverage_config: {
        Args: never
        Returns: {
          assistance_phone_primary: string
          assistance_phone_secondary: string
          coverage_status: string
        }[]
      }
      get_unread_message_count: { Args: { user_id: string }; Returns: number }
      get_vehicle_unavailable_ranges: {
        Args: {
          p_from_date?: string
          p_timezone?: string
          p_to_date?: string
          p_vehicle_id: string
        }
        Returns: {
          end_date: string
          free_from: string
          free_until: string
          reason: string
          source_id: string
          start_date: string
        }[]
      }
      get_vehicle_trip_reviews: {
        Args: { p_limit?: number; p_vehicle_id: string }
        Returns: {
          comment: string
          created_at: string
          id: string
          rating: number
          rental_id: string
          reviewer_avatar_url: string
          reviewer_display_name: string
        }[]
      }
      get_visible_reviews: {
        Args: { p_target: string }
        Returns: {
          comment: string | null
          created_at: string
          hidden_reason: string | null
          id: string
          is_visible: boolean
          rating: number
          rental_id: string
          response: string | null
          response_at: string | null
          reviewer_id: string
          updated_at: string
        }[]
        SetofOptions: {
          from: '*'
          to: 'reviews'
          isOneToOne: false
          isSetofReturn: true
        }
      }
      handover_presence_status: {
        Args: { p_inspection_type: string; p_rental_id: string }
        Returns: boolean
      }
      gettransactionid: { Args: never; Returns: unknown }
      has_complete_verification: {
        Args: { p_profile_id: string }
        Returns: boolean
      }
      has_host_identity_for_listing: {
        Args: { p_profile_id: string }
        Returns: boolean
      }
      has_trusted_vehicle_activation_bypass: {
        Args: { p_vehicle: Database['public']['Tables']['vehicles']['Row'] }
        Returns: boolean
      }
      has_verified_contacts: {
        Args: { p_profile_id: string }
        Returns: boolean
      }
      is_admin: { Args: never; Returns: boolean }
      is_conversation_participant: {
        Args: { conv_id: string }
        Returns: boolean
      }
      is_home_location_in_el_salvador: {
        Args: { p_location: unknown }
        Returns: boolean
      }
      is_internal_contact_email: { Args: { p_email: string }; Returns: boolean }
      is_marketplace_visible_vehicle: {
        Args: { p_vehicle: Database['public']['Tables']['vehicles']['Row'] }
        Returns: boolean
      }
      is_org_manager: { Args: { org_id: string }; Returns: boolean }
      is_org_member: { Args: { org_id: string }; Returns: boolean }
      is_profile_complete_for_welcome_promo: {
        Args: { p_profile_id: string }
        Returns: boolean
      }
      is_profile_restricted: {
        Args: { p_profile_id: string }
        Returns: boolean
      }
      is_rental_participant: { Args: { rental_id: string }; Returns: boolean }
      is_support: { Args: never; Returns: boolean }
      is_user_blocked: {
        Args: { target_id: string; viewer_id: string }
        Returns: boolean
      }
      is_valid_sv_nit: { Args: { p_value: string }; Returns: boolean }
      late_fee_preview: {
        Args: { p_rental_id: string }
        Returns: {
          end_at: string
          fee_after_grace_cents: number
          fee_cents: number
          grace_ends_at: string
          minutes_late: number
        }[]
      }
      latest_aml_screening: {
        Args: { p_profile_id: string }
        Returns: {
          answered_at: string
          outcome: string
          reason: string
          requested_at: string
        }[]
      }
      list_organization_pending_invites: {
        Args: { p_organization_id: string }
        Returns: Json
      }
      list_organization_payout_accounts: {
        Args: { p_organization_id: string }
        Returns: {
          account_holder_name: string | null
          account_number_masked: string | null
          bank_name: string | null
          id: string
          is_default: boolean
          is_verified: boolean
        }[]
      }
      list_my_payout_accounts: {
        Args: never
        Returns: {
          account_holder_name: string | null
          account_number_masked: string | null
          bank_name: string | null
          id: string
          is_default: boolean
          is_verified: boolean
        }[]
      }
      list_my_vehicle_listing_blockers: {
        Args: never
        Returns: {
          vehicle_id: string
          blockers: string[]
        }[]
      }
      listing_publish_blockers: {
        Args: { p_vehicle_id: string }
        Returns: string[]
      }
      longtransactionsenabled: { Args: never; Returns: boolean }
      nearby_vehicles: {
        Args: {
          lat: number
          limit_count?: number
          lng: number
          radius_km?: number
        }
        Returns: {
          category: Database['public']['Enums']['vehicle_category']
          daily_rate_cents: number
          distance_km: number
          home_location: unknown
          host_id: string
          hourly_rate_cents: number
          id: string
          is_instant_book: boolean
          make_name: string
          model_name: string
          primary_image_blurhash: string
          primary_image_url: string
          rating_avg: number
          rating_count: number
          rented_days: number
          status: Database['public']['Enums']['vehicle_status']
          title: string
          transmission: Database['public']['Enums']['transmission_type']
          year: number
        }[]
      }
      ni_leer_payout_account_full: {
        Args: { p_org_id?: string; p_profile_id?: string }
        Returns: {
          account_holder_name: string
          account_number_full: string
          account_type: string
          bank_code: string
          bank_name: string
          country_code: string
          currency: string
          is_verified: boolean
        }[]
      }
      normalize_auth_phone: { Args: { p_phone: string }; Returns: string }
      normalize_sv_nit: { Args: { p_value: string }; Returns: string }
      notify_hosts_of_expired_vehicle_papers: { Args: never; Returns: number }
      notify_nearby_users_new_vehicle: {
        Args: { p_vehicle_id: string }
        Returns: number
      }
      notify_ops_of_deposits_due: { Args: never; Returns: number }
      notify_phone_verified: {
        Args: { p_manual?: boolean; p_user_id: string }
        Returns: undefined
      }
      notify_stale_build_once: {
        Args: { p_platform: string; p_profile_id: string; p_target_build: number }
        Returns: boolean
      }
      notify_stale_builds_batch: {
        Args: { p_latest_build: number; p_limit?: number; p_platform: string }
        Returns: {
          notificados: number
          pendientes: number
        }[]
      }
      org_member_profiles: { Args: { p_organization_id: string }; Returns: Json }
      owns_vehicle: { Args: { vehicle_id: string }; Returns: boolean }
      partner_review_confirm_session: {
        Args: { p_code_hash: string; p_session_id: string }
        Returns: Json
      }
      partner_review_decide_aml_declaration: {
        Args: {
          p_decision: string
          p_declaration_id: string
          p_reason?: string
          p_session_id: string
        }
        Returns: Json
      }
      partner_review_mark_aml_exported: {
        Args: {
          p_declaration_ids: string[]
          p_reference: string
          p_session_id: string
        }
        Returns: Json
      }
      partner_review_session_is_confirmed: {
        Args: { p_session_id: string }
        Returns: boolean
      }
      partner_review_submit_inspection_decision: {
        Args: {
          p_code_hash: string
          p_comment?: string
          p_decision: string
          p_session_id: string
        }
        Returns: undefined
      }
      pending_trip_review_for_viewer: {
        Args: never
        Returns: {
          counterparty_avatar: string
          counterparty_id: string
          counterparty_name: string
          ended_at: string
          rental_id: string
          reviewer_role: string
          started_at: string
          vehicle_id: string
          vehicle_image: string
          vehicle_title: string
        }[]
      }
      populate_geometry_columns:
        | { Args: { tbl_oid: unknown; use_typmod?: boolean }; Returns: number }
        | { Args: { use_typmod?: boolean }; Returns: string }
      postgis_constraint_dims: {
        Args: { geomcolumn: string; geomschema: string; geomtable: string }
        Returns: number
      }
      postgis_constraint_srid: {
        Args: { geomcolumn: string; geomschema: string; geomtable: string }
        Returns: number
      }
      postgis_constraint_type: {
        Args: { geomcolumn: string; geomschema: string; geomtable: string }
        Returns: string
      }
      postgis_extensions_upgrade: { Args: never; Returns: string }
      postgis_full_version: { Args: never; Returns: string }
      postgis_geos_version: { Args: never; Returns: string }
      postgis_lib_build_date: { Args: never; Returns: string }
      postgis_lib_revision: { Args: never; Returns: string }
      postgis_lib_version: { Args: never; Returns: string }
      postgis_libjson_version: { Args: never; Returns: string }
      postgis_liblwgeom_version: { Args: never; Returns: string }
      postgis_libprotobuf_version: { Args: never; Returns: string }
      postgis_libxml_version: { Args: never; Returns: string }
      postgis_proj_version: { Args: never; Returns: string }
      postgis_scripts_build_date: { Args: never; Returns: string }
      postgis_scripts_installed: { Args: never; Returns: string }
      postgis_scripts_released: { Args: never; Returns: string }
      postgis_svn_version: { Args: never; Returns: string }
      postgis_type_name: {
        Args: {
          coord_dimension: number
          geomname: string
          use_new_name?: boolean
        }
        Returns: string
      }
      postgis_version: { Args: never; Returns: string }
      postgis_wagyu_version: { Args: never; Returns: string }
      process_first_trip_activation_notifications: {
        Args: never
        Returns: Json
      }
      process_host_monthly_digest: { Args: never; Returns: Json }
      process_lifecycle_and_recovery_notifications: {
        Args: never
        Returns: Json
      }
      process_pending_review_reminders: { Args: never; Returns: Json }
      process_weekend_getaway_promotions: { Args: never; Returns: Json }
      provision_qa_account: {
        Args: { p_email: string; p_password: string; p_phone?: string }
        Returns: string
      }
      record_deposit_outcome: {
        Args: {
          p_actor?: string
          p_amount_cents?: number
          p_kind: string
          p_reason?: string
          p_rental_id: string
        }
        Returns: number
      }
      respond_to_paid_rental: {
        Args: { p_rental_id: string; p_decision: 'accept' | 'decline'; p_reason?: string }
        Returns: Json
      }
      my_host_gateway_fee_claim: {
        Args: { p_rental_id: string }
        Returns: Json
      }
      dispute_host_gateway_fee: {
        Args: { p_rental_id: string; p_reason: string }
        Returns: boolean
      }
      current_rental_terms: { Args: never; Returns: Json }
      accept_current_rental_terms: {
        Args: { p_expected_version: string }
        Returns: Json
      }
      accept_noticed_rental_terms: {
        Args: { p_expected_version: string }
        Returns: Json
      }
      record_promo_use: {
        Args: {
          p_discount_cents: number
          p_profile_id: string
          p_promo_id: string
          p_rental_id: string
        }
        Returns: boolean
      }
      replace_vehicle_blocked_dates: {
        Args: {
          p_blocked_ranges?: Json
          p_vehicle_id: string
          p_window_end: string
          p_window_start: string
        }
        Returns: {
          created_at: string
          end_date: string
          id: string
          is_available: boolean
          reason: string
          start_date: string
          updated_at: string
          vehicle_id: string
        }[]
      }
      request_account_deletion: {
        Args: { reason?: string }
        Returns: undefined
      }
      record_consent: {
        Args: {
          p_consent_type: string
          p_granted: boolean
          p_purpose: string
          p_legal_basis: string
          p_consent_form_version: string
          p_consent_form_url?: string | null
        }
        Returns: undefined
      }
      required_vehicle_listing_photo_requirement_ids: {
        Args: never
        Returns: string[]
      }
      retention_ops_snapshot: { Args: never; Returns: Json }
      sanitize_provider_response: {
        Args: { provider: string; raw: Json }
        Returns: Json
      }
      search_vehicles: {
        Args: {
          p_category?: Database['public']['Enums']['vehicle_category']
          p_end_date?: string
          p_lat?: number
          p_limit?: number
          p_lng?: number
          p_max_price?: number
          p_min_price?: number
          p_min_year?: number
          p_offset?: number
          p_radius_km?: number
          p_required_features?: string[]
          p_search_query?: string
          p_start_date?: string
          p_transmission?: Database['public']['Enums']['transmission_type']
        }
        Returns: {
          category: Database['public']['Enums']['vehicle_category']
          daily_rate_cents: number
          distance_km: number
          home_location: unknown
          host_id: string
          hourly_rate_cents: number
          id: string
          is_instant_book: boolean
          make_name: string
          model_name: string
          primary_image_blurhash: string
          primary_image_url: string
          rating_avg: number
          rating_count: number
          rented_days: number
          shadow_rank_penalty: number
          status: Database['public']['Enums']['vehicle_status']
          title: string
          transmission: Database['public']['Enums']['transmission_type']
          year: number
        }[]
      }
      set_default_payment_method: {
        Args: { p_payment_method_id: string }
        Returns: {
          billing_country: string | null
          billing_name: string | null
          card_brand: string | null
          card_exp_month: number | null
          card_exp_year: number | null
          card_fingerprint: string | null
          card_last4: string | null
          created_at: string
          deactivated_at: string | null
          id: string
          is_active: boolean
          is_default: boolean
          label: string | null
          method_type: Database['public']['Enums']['payment_method_type']
          profile_id: string
          provider: string
          provider_token: string
          updated_at: string
        }
        SetofOptions: {
          from: '*'
          to: 'payment_methods'
          isOneToOne: true
          isSetofReturn: false
        }
      }
      set_default_payout_account: {
        Args: { p_account_id: string; p_organization_id?: string | null }
        Returns: undefined
      }
      st_3dclosestpoint: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_3ddistance: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      st_3dintersects: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      st_3dlongestline: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_3dmakebox: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_3dmaxdistance: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      st_3dshortestline: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_addpoint: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_angle:
        | { Args: { line1: unknown; line2: unknown }; Returns: number }
        | {
            Args: { pt1: unknown; pt2: unknown; pt3: unknown; pt4?: unknown }
            Returns: number
          }
      st_area:
        | { Args: { geog: unknown; use_spheroid?: boolean }; Returns: number }
        | { Args: { '': string }; Returns: number }
      st_asencodedpolyline: {
        Args: { geom: unknown; nprecision?: number }
        Returns: string
      }
      st_asewkt: { Args: { '': string }; Returns: string }
      st_asgeojson:
        | {
            Args: { geog: unknown; maxdecimaldigits?: number; options?: number }
            Returns: string
          }
        | {
            Args: { geom: unknown; maxdecimaldigits?: number; options?: number }
            Returns: string
          }
        | {
            Args: {
              geom_column?: string
              maxdecimaldigits?: number
              pretty_bool?: boolean
              r: Record<string, unknown>
            }
            Returns: string
          }
        | { Args: { '': string }; Returns: string }
      st_asgml:
        | {
            Args: {
              geog: unknown
              id?: string
              maxdecimaldigits?: number
              nprefix?: string
              options?: number
            }
            Returns: string
          }
        | {
            Args: { geom: unknown; maxdecimaldigits?: number; options?: number }
            Returns: string
          }
        | { Args: { '': string }; Returns: string }
        | {
            Args: {
              geog: unknown
              id?: string
              maxdecimaldigits?: number
              nprefix?: string
              options?: number
              version: number
            }
            Returns: string
          }
        | {
            Args: {
              geom: unknown
              id?: string
              maxdecimaldigits?: number
              nprefix?: string
              options?: number
              version: number
            }
            Returns: string
          }
      st_askml:
        | {
            Args: { geog: unknown; maxdecimaldigits?: number; nprefix?: string }
            Returns: string
          }
        | {
            Args: { geom: unknown; maxdecimaldigits?: number; nprefix?: string }
            Returns: string
          }
        | { Args: { '': string }; Returns: string }
      st_aslatlontext: {
        Args: { geom: unknown; tmpl?: string }
        Returns: string
      }
      st_asmarc21: { Args: { format?: string; geom: unknown }; Returns: string }
      st_asmvtgeom: {
        Args: {
          bounds: unknown
          buffer?: number
          clip_geom?: boolean
          extent?: number
          geom: unknown
        }
        Returns: unknown
      }
      st_assvg:
        | {
            Args: { geog: unknown; maxdecimaldigits?: number; rel?: number }
            Returns: string
          }
        | {
            Args: { geom: unknown; maxdecimaldigits?: number; rel?: number }
            Returns: string
          }
        | { Args: { '': string }; Returns: string }
      st_astext: { Args: { '': string }; Returns: string }
      st_astwkb:
        | {
            Args: {
              geom: unknown
              prec?: number
              prec_m?: number
              prec_z?: number
              with_boxes?: boolean
              with_sizes?: boolean
            }
            Returns: string
          }
        | {
            Args: {
              geom: unknown[]
              ids: number[]
              prec?: number
              prec_m?: number
              prec_z?: number
              with_boxes?: boolean
              with_sizes?: boolean
            }
            Returns: string
          }
      st_asx3d: {
        Args: { geom: unknown; maxdecimaldigits?: number; options?: number }
        Returns: string
      }
      st_azimuth:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: number }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: number }
      st_boundingdiagonal: {
        Args: { fits?: boolean; geom: unknown }
        Returns: unknown
      }
      st_buffer:
        | {
            Args: { geom: unknown; options?: string; radius: number }
            Returns: unknown
          }
        | {
            Args: { geom: unknown; quadsegs: number; radius: number }
            Returns: unknown
          }
      st_centroid: { Args: { '': string }; Returns: unknown }
      st_clipbybox2d: {
        Args: { box: unknown; geom: unknown }
        Returns: unknown
      }
      st_closestpoint: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_collect: { Args: { geom1: unknown; geom2: unknown }; Returns: unknown }
      st_concavehull: {
        Args: {
          param_allow_holes?: boolean
          param_geom: unknown
          param_pctconvex: number
        }
        Returns: unknown
      }
      st_contains: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      st_containsproperly: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      st_coorddim: { Args: { geometry: unknown }; Returns: number }
      st_coveredby:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      st_covers:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      st_crosses: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      st_curvetoline: {
        Args: { flags?: number; geom: unknown; tol?: number; toltype?: number }
        Returns: unknown
      }
      st_delaunaytriangles: {
        Args: { flags?: number; g1: unknown; tolerance?: number }
        Returns: unknown
      }
      st_difference: {
        Args: { geom1: unknown; geom2: unknown; gridsize?: number }
        Returns: unknown
      }
      st_disjoint: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      st_distance:
        | {
            Args: { geog1: unknown; geog2: unknown; use_spheroid?: boolean }
            Returns: number
          }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: number }
      st_distancesphere:
        | { Args: { geom1: unknown; geom2: unknown }; Returns: number }
        | {
            Args: { geom1: unknown; geom2: unknown; radius: number }
            Returns: number
          }
      st_distancespheroid: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      st_dwithin: {
        Args: {
          geog1: unknown
          geog2: unknown
          tolerance: number
          use_spheroid?: boolean
        }
        Returns: boolean
      }
      st_equals: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      st_expand:
        | { Args: { box: unknown; dx: number; dy: number }; Returns: unknown }
        | {
            Args: { box: unknown; dx: number; dy: number; dz?: number }
            Returns: unknown
          }
        | {
            Args: {
              dm?: number
              dx: number
              dy: number
              dz?: number
              geom: unknown
            }
            Returns: unknown
          }
      st_force3d: { Args: { geom: unknown; zvalue?: number }; Returns: unknown }
      st_force3dm: {
        Args: { geom: unknown; mvalue?: number }
        Returns: unknown
      }
      st_force3dz: {
        Args: { geom: unknown; zvalue?: number }
        Returns: unknown
      }
      st_force4d: {
        Args: { geom: unknown; mvalue?: number; zvalue?: number }
        Returns: unknown
      }
      st_generatepoints:
        | { Args: { area: unknown; npoints: number }; Returns: unknown }
        | {
            Args: { area: unknown; npoints: number; seed: number }
            Returns: unknown
          }
      st_geogfromtext: { Args: { '': string }; Returns: unknown }
      st_geographyfromtext: { Args: { '': string }; Returns: unknown }
      st_geohash:
        | { Args: { geog: unknown; maxchars?: number }; Returns: string }
        | { Args: { geom: unknown; maxchars?: number }; Returns: string }
      st_geomcollfromtext: { Args: { '': string }; Returns: unknown }
      st_geometricmedian: {
        Args: {
          fail_if_not_converged?: boolean
          g: unknown
          max_iter?: number
          tolerance?: number
        }
        Returns: unknown
      }
      st_geometryfromtext: { Args: { '': string }; Returns: unknown }
      st_geomfromewkt: { Args: { '': string }; Returns: unknown }
      st_geomfromgeojson:
        | { Args: { '': Json }; Returns: unknown }
        | { Args: { '': Json }; Returns: unknown }
        | { Args: { '': string }; Returns: unknown }
      st_geomfromgml: { Args: { '': string }; Returns: unknown }
      st_geomfromkml: { Args: { '': string }; Returns: unknown }
      st_geomfrommarc21: { Args: { marc21xml: string }; Returns: unknown }
      st_geomfromtext: { Args: { '': string }; Returns: unknown }
      st_gmltosql: { Args: { '': string }; Returns: unknown }
      st_hasarc: { Args: { geometry: unknown }; Returns: boolean }
      st_hausdorffdistance: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      st_hexagon: {
        Args: { cell_i: number; cell_j: number; origin?: unknown; size: number }
        Returns: unknown
      }
      st_hexagongrid: {
        Args: { bounds: unknown; size: number }
        Returns: Record<string, unknown>[]
      }
      st_interpolatepoint: {
        Args: { line: unknown; point: unknown }
        Returns: number
      }
      st_intersection: {
        Args: { geom1: unknown; geom2: unknown; gridsize?: number }
        Returns: unknown
      }
      st_intersects:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      st_isvaliddetail: {
        Args: { flags?: number; geom: unknown }
        Returns: Database['public']['CompositeTypes']['valid_detail']
        SetofOptions: {
          from: '*'
          to: 'valid_detail'
          isOneToOne: true
          isSetofReturn: false
        }
      }
      st_length:
        | { Args: { geog: unknown; use_spheroid?: boolean }; Returns: number }
        | { Args: { '': string }; Returns: number }
      st_letters: { Args: { font?: Json; letters: string }; Returns: unknown }
      st_linecrossingdirection: {
        Args: { line1: unknown; line2: unknown }
        Returns: number
      }
      st_linefromencodedpolyline: {
        Args: { nprecision?: number; txtin: string }
        Returns: unknown
      }
      st_linefromtext: { Args: { '': string }; Returns: unknown }
      st_linelocatepoint: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      st_linetocurve: { Args: { geometry: unknown }; Returns: unknown }
      st_locatealong: {
        Args: { geometry: unknown; leftrightoffset?: number; measure: number }
        Returns: unknown
      }
      st_locatebetween: {
        Args: {
          frommeasure: number
          geometry: unknown
          leftrightoffset?: number
          tomeasure: number
        }
        Returns: unknown
      }
      st_locatebetweenelevations: {
        Args: { fromelevation: number; geometry: unknown; toelevation: number }
        Returns: unknown
      }
      st_longestline: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_makebox2d: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_makeline: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_makevalid: {
        Args: { geom: unknown; params: string }
        Returns: unknown
      }
      st_maxdistance: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: number
      }
      st_minimumboundingcircle: {
        Args: { inputgeom: unknown; segs_per_quarter?: number }
        Returns: unknown
      }
      st_mlinefromtext: { Args: { '': string }; Returns: unknown }
      st_mpointfromtext: { Args: { '': string }; Returns: unknown }
      st_mpolyfromtext: { Args: { '': string }; Returns: unknown }
      st_multilinestringfromtext: { Args: { '': string }; Returns: unknown }
      st_multipointfromtext: { Args: { '': string }; Returns: unknown }
      st_multipolygonfromtext: { Args: { '': string }; Returns: unknown }
      st_node: { Args: { g: unknown }; Returns: unknown }
      st_normalize: { Args: { geom: unknown }; Returns: unknown }
      st_offsetcurve: {
        Args: { distance: number; line: unknown; params?: string }
        Returns: unknown
      }
      st_orderingequals: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      st_overlaps: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: boolean
      }
      st_perimeter: {
        Args: { geog: unknown; use_spheroid?: boolean }
        Returns: number
      }
      st_pointfromtext: { Args: { '': string }; Returns: unknown }
      st_pointm: {
        Args: {
          mcoordinate: number
          srid?: number
          xcoordinate: number
          ycoordinate: number
        }
        Returns: unknown
      }
      st_pointz: {
        Args: {
          srid?: number
          xcoordinate: number
          ycoordinate: number
          zcoordinate: number
        }
        Returns: unknown
      }
      st_pointzm: {
        Args: {
          mcoordinate: number
          srid?: number
          xcoordinate: number
          ycoordinate: number
          zcoordinate: number
        }
        Returns: unknown
      }
      st_polyfromtext: { Args: { '': string }; Returns: unknown }
      st_polygonfromtext: { Args: { '': string }; Returns: unknown }
      st_project: {
        Args: { azimuth: number; distance: number; geog: unknown }
        Returns: unknown
      }
      st_quantizecoordinates: {
        Args: {
          g: unknown
          prec_m?: number
          prec_x: number
          prec_y?: number
          prec_z?: number
        }
        Returns: unknown
      }
      st_reduceprecision: {
        Args: { geom: unknown; gridsize: number }
        Returns: unknown
      }
      st_relate: { Args: { geom1: unknown; geom2: unknown }; Returns: string }
      st_removerepeatedpoints: {
        Args: { geom: unknown; tolerance?: number }
        Returns: unknown
      }
      st_segmentize: {
        Args: { geog: unknown; max_segment_length: number }
        Returns: unknown
      }
      st_setsrid:
        | { Args: { geog: unknown; srid: number }; Returns: unknown }
        | { Args: { geom: unknown; srid: number }; Returns: unknown }
      st_sharedpaths: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_shortestline: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_simplifypolygonhull: {
        Args: { geom: unknown; is_outer?: boolean; vertex_fraction: number }
        Returns: unknown
      }
      st_split: { Args: { geom1: unknown; geom2: unknown }; Returns: unknown }
      st_square: {
        Args: { cell_i: number; cell_j: number; origin?: unknown; size: number }
        Returns: unknown
      }
      st_squaregrid: {
        Args: { bounds: unknown; size: number }
        Returns: Record<string, unknown>[]
      }
      st_srid:
        | { Args: { geog: unknown }; Returns: number }
        | { Args: { geom: unknown }; Returns: number }
      st_subdivide: {
        Args: { geom: unknown; gridsize?: number; maxvertices?: number }
        Returns: unknown[]
      }
      st_swapordinates: {
        Args: { geom: unknown; ords: unknown }
        Returns: unknown
      }
      st_symdifference: {
        Args: { geom1: unknown; geom2: unknown; gridsize?: number }
        Returns: unknown
      }
      st_symmetricdifference: {
        Args: { geom1: unknown; geom2: unknown }
        Returns: unknown
      }
      st_tileenvelope: {
        Args: {
          bounds?: unknown
          margin?: number
          x: number
          y: number
          zoom: number
        }
        Returns: unknown
      }
      st_touches: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      st_transform:
        | {
            Args: { from_proj: string; geom: unknown; to_proj: string }
            Returns: unknown
          }
        | {
            Args: { from_proj: string; geom: unknown; to_srid: number }
            Returns: unknown
          }
        | { Args: { geom: unknown; to_proj: string }; Returns: unknown }
      st_triangulatepolygon: { Args: { g1: unknown }; Returns: unknown }
      st_union:
        | { Args: { geom1: unknown; geom2: unknown }; Returns: unknown }
        | {
            Args: { geom1: unknown; geom2: unknown; gridsize: number }
            Returns: unknown
          }
      st_voronoilines: {
        Args: { extend_to?: unknown; g1: unknown; tolerance?: number }
        Returns: unknown
      }
      st_voronoipolygons: {
        Args: { extend_to?: unknown; g1: unknown; tolerance?: number }
        Returns: unknown
      }
      st_within: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean }
      st_wkbtosql: { Args: { wkb: string }; Returns: unknown }
      st_wkttosql: { Args: { '': string }; Returns: unknown }
      st_wrapx: {
        Args: { geom: unknown; move: number; wrap: number }
        Returns: unknown
      }
      stale_build_push_targets: {
        Args: { p_latest_build: number; p_platform: string }
        Returns: {
          profile_id: string
        }[]
      }
      submit_trip_review: {
        Args: { p_comment?: string; p_rating: number; p_rental_id: string }
        Returns: {
          comment: string
          created_at: string
          rating: number
          rental_id: string
          review_id: string
          reviewer_role: string
          vehicle_id: string
        }[]
      }
      unlockrows: { Args: { '': string }; Returns: number }
      updategeometrysrid: {
        Args: {
          catalogn_name: string
          column_name: string
          new_srid_in: number
          schema_name: string
          table_name: string
        }
        Returns: string
      }
      vehicle_has_complete_live_asr_photos: {
        Args: { p_vehicle_id: string }
        Returns: boolean
      }
      vehicle_ownership_reconciliation_for: {
        Args: { p_vehicle_id: string }
        Returns: {
          declared: string
          expected_name: string
          expected_source: string
          tarjeta_owner: string
          verdict: string
        }[]
      }
    }
    Enums: {
      aml_declaration_status:
        | 'draft'
        | 'submitted'
        | 'under_review'
        | 'approved'
        | 'rejected'
        | 'needs_update'
      aml_fund_origin: 'salaried' | 'independent_merchant' | 'independent_professional'
      aml_gender: 'female' | 'male' | 'other' | 'prefer_not_to_say'
      aml_income_range:
        | '1_800'
        | '801_2000'
        | '2001_4500'
        | '4501_6500'
        | '6501_10000'
        | '10001_plus'
      arco_request_type:
        | 'access'
        | 'rectification'
        | 'cancellation'
        | 'opposition'
        | 'portability'
        | 'limitation'
      arco_status: 'received' | 'in_progress' | 'completed' | 'denied' | 'extended'
      cancellation_party: 'guest' | 'host' | 'platform' | 'system'
      claim_status:
        | 'draft'
        | 'submitted'
        | 'under_review'
        | 'approved'
        | 'rejected'
        | 'paid'
        | 'appealed'
      damage_severity: 'minor' | 'moderate' | 'severe' | 'total_loss'
      dispute_category:
        | 'vehicle_damage'
        | 'excessive_mileage'
        | 'fuel_level'
        | 'cleanliness'
        | 'late_return'
        | 'missing_items'
        | 'billing'
        | 'other'
      dispute_settlement_action: 'void' | 'capture' | 'partial_refund'
      dispute_settlement_status: 'pending' | 'in_progress' | 'completed' | 'failed'
      dispute_status:
        | 'open'
        | 'awaiting_response'
        | 'under_review'
        | 'resolved_claimant'
        | 'resolved_respondent'
        | 'resolved_split'
        | 'withdrawn'
      dte_status: 'pending' | 'generated' | 'sent' | 'accepted' | 'rejected' | 'cancelled'
      dte_type: 'factura' | 'ccf' | 'nota_credito' | 'nota_debito' | 'retencion'
      fuel_type: 'gasoline' | 'diesel' | 'electric' | 'hybrid' | 'plugin_hybrid'
      id_document_type: 'dui' | 'passport' | 'license' | 'cedula' | 'nie'
      insurance_model: 'platform' | 'fleet' | 'hybrid' | 'none'
      ledger_entry_type:
        | 'rental_payment'
        | 'security_deposit'
        | 'deposit_release'
        | 'deposit_claim'
        | 'host_payout'
        | 'platform_fee'
        | 'refund'
        | 'adjustment'
        | 'insurance_premium'
        | 'damage_charge'
        | 'late_fee'
        | 'cleaning_fee'
        | 'fuel_charge'
        | 'multa_charge'
        | 'pending_manual_refund'
        | 'cancellation_no_refund'
      license_class: 'liviana' | 'pesada' | 'motocicleta' | 'especial'
      message_type: 'text' | 'image' | 'system' | 'location' | 'document'
      multa_liability: 'guest' | 'host' | 'platform' | 'pending_determination'
      multa_status:
        | 'received'
        | 'notified'
        | 'acknowledged'
        | 'disputed'
        | 'paid'
        | 'transferred'
        | 'closed'
      notification_channel: 'push' | 'email' | 'sms' | 'in_app'
      payment_method_type: 'card' | 'bank_transfer' | 'wallet' | 'cash'
      payment_status:
        | 'pending'
        | 'authorized'
        | 'captured'
        | 'failed'
        | 'refunded'
        | 'partially_refunded'
        | 'disputed'
      payout_status: 'pending' | 'processing' | 'completed' | 'failed' | 'reversed'
      rental_status:
        | 'pending_payment'
        | 'awaiting_host'
        | 'confirmed'
        | 'pending_pickup'
        | 'active'
        | 'pending_return'
        | 'completed'
        | 'cancelled'
        | 'disputed'
      transmission_type: 'automatic' | 'manual' | 'cvt' | 'dct'
      user_role: 'guest' | 'host' | 'admin' | 'support' | 'fleet_manager' | 'fleet_driver'
      vehicle_category:
        | 'sedan'
        | 'suv'
        | 'pickup'
        | 'van'
        | 'compact'
        | 'luxury'
        | 'sports'
        | 'motorcycle'
        | 'heavy_duty'
        | 'recreational'
        | 'microbus'
      vehicle_status: 'draft' | 'pending_review' | 'active' | 'paused' | 'suspended' | 'archived'
      verification_status:
        | 'pending'
        | 'submitted'
        | 'approved'
        | 'rejected'
        | 'expired'
        | 'superseded'
    }
    CompositeTypes: {
      geometry_dump: {
        path: number[] | null
        geom: unknown
      }
      valid_detail: {
        valid: boolean | null
        reason: string | null
        location: unknown
      }
    }
  }
}

type DatabaseWithoutInternals = Omit<Database, '__InternalSupabase'>

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, 'public'>]

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema['Tables'] & DefaultSchema['Views'])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables'] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Views'])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables'] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Views'])[TableName] extends {
      Row: infer R
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema['Tables'] & DefaultSchema['Views'])
    ? (DefaultSchema['Tables'] & DefaultSchema['Views'])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R
      }
      ? R
      : never
    : never

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema['Tables']
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables']
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables'][TableName] extends {
      Insert: infer I
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema['Tables']
    ? DefaultSchema['Tables'][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I
      }
      ? I
      : never
    : never

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema['Tables']
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables']
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables'][TableName] extends {
      Update: infer U
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema['Tables']
    ? DefaultSchema['Tables'][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U
      }
      ? U
      : never
    : never

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema['Enums']
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions['schema']]['Enums']
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions['schema']]['Enums'][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema['Enums']
    ? DefaultSchema['Enums'][DefaultSchemaEnumNameOrOptions]
    : never

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema['CompositeTypes']
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions['schema']]['CompositeTypes']
    : never = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions['schema']]['CompositeTypes'][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema['CompositeTypes']
    ? DefaultSchema['CompositeTypes'][PublicCompositeTypeNameOrOptions]
    : never

export const Constants = {
  public: {
    Enums: {
      aml_declaration_status: [
        'draft',
        'submitted',
        'under_review',
        'approved',
        'rejected',
        'needs_update',
      ],
      aml_fund_origin: ['salaried', 'independent_merchant', 'independent_professional'],
      aml_gender: ['female', 'male', 'other', 'prefer_not_to_say'],
      aml_income_range: ['1_800', '801_2000', '2001_4500', '4501_6500', '6501_10000', '10001_plus'],
      arco_request_type: [
        'access',
        'rectification',
        'cancellation',
        'opposition',
        'portability',
        'limitation',
      ],
      arco_status: ['received', 'in_progress', 'completed', 'denied', 'extended'],
      cancellation_party: ['guest', 'host', 'platform', 'system'],
      claim_status: [
        'draft',
        'submitted',
        'under_review',
        'approved',
        'rejected',
        'paid',
        'appealed',
      ],
      damage_severity: ['minor', 'moderate', 'severe', 'total_loss'],
      dispute_category: [
        'vehicle_damage',
        'excessive_mileage',
        'fuel_level',
        'cleanliness',
        'late_return',
        'missing_items',
        'billing',
        'other',
      ],
      dispute_settlement_action: ['void', 'capture', 'partial_refund'],
      dispute_settlement_status: ['pending', 'in_progress', 'completed', 'failed'],
      dispute_status: [
        'open',
        'awaiting_response',
        'under_review',
        'resolved_claimant',
        'resolved_respondent',
        'resolved_split',
        'withdrawn',
      ],
      dte_status: ['pending', 'generated', 'sent', 'accepted', 'rejected', 'cancelled'],
      dte_type: ['factura', 'ccf', 'nota_credito', 'nota_debito', 'retencion'],
      fuel_type: ['gasoline', 'diesel', 'electric', 'hybrid', 'plugin_hybrid'],
      id_document_type: ['dui', 'passport', 'license', 'cedula', 'nie'],
      insurance_model: ['platform', 'fleet', 'hybrid', 'none'],
      ledger_entry_type: [
        'rental_payment',
        'security_deposit',
        'deposit_release',
        'deposit_claim',
        'host_payout',
        'platform_fee',
        'refund',
        'adjustment',
        'insurance_premium',
        'damage_charge',
        'late_fee',
        'cleaning_fee',
        'fuel_charge',
        'multa_charge',
        'pending_manual_refund',
        'cancellation_no_refund',
      ],
      license_class: ['liviana', 'pesada', 'motocicleta', 'especial'],
      message_type: ['text', 'image', 'system', 'location', 'document'],
      multa_liability: ['guest', 'host', 'platform', 'pending_determination'],
      multa_status: [
        'received',
        'notified',
        'acknowledged',
        'disputed',
        'paid',
        'transferred',
        'closed',
      ],
      notification_channel: ['push', 'email', 'sms', 'in_app'],
      payment_method_type: ['card', 'bank_transfer', 'wallet', 'cash'],
      payment_status: [
        'pending',
        'authorized',
        'captured',
        'failed',
        'refunded',
        'partially_refunded',
        'disputed',
      ],
      payout_status: ['pending', 'processing', 'completed', 'failed', 'reversed'],
      rental_status: [
        'pending_payment',
        'awaiting_host',
        'confirmed',
        'pending_pickup',
        'active',
        'pending_return',
        'completed',
        'cancelled',
        'disputed',
      ],
      transmission_type: ['automatic', 'manual', 'cvt', 'dct'],
      user_role: ['guest', 'host', 'admin', 'support', 'fleet_manager', 'fleet_driver'],
      vehicle_category: [
        'sedan',
        'suv',
        'pickup',
        'van',
        'compact',
        'luxury',
        'sports',
        'motorcycle',
        'heavy_duty',
        'recreational',
        'microbus',
      ],
      vehicle_status: ['draft', 'pending_review', 'active', 'paused', 'suspended', 'archived'],
      verification_status: [
        'pending',
        'submitted',
        'approved',
        'rejected',
        'expired',
        'superseded',
      ],
    },
  },
} as const
